/**
 * @esf/shared
 * ESF Digital Product Passport Platform
 * Type-driven shared types, schemas, and utilities.
 *
 * Architecture: Types are the single source of truth.
 * - Const arrays define enum values (runtime + type inference)
 * - Interfaces use derived types
 * - Zod schemas validate at runtime
 * - FieldMetadata drives UI generation
 */
import { z } from "zod";
// ============================================================================
// CONSTANTS (as const arrays for runtime + type inference)
// ============================================================================
// Product Categories (universal categories for street furniture - ESPR/DPP aligned)
export const ProductCategories = [
    "seating", // Benches, seats, perches
    "litter_recycling", // Litter bins, recycling units, waste management
    "shelters", // Bus shelters, waiting shelters, canopies
    "cycle", // Bicycle parking, cycle stands, cycle shelters
    "bollards_barriers", // Static bollards, rising bollards, road blockers, barriers
    "signage_display", // Digital signage, wayfinding, notice boards, totems
    "solar_power", // Solar panels, EV charging, power units, SmartFlower
    "planters", // Planters, tree guards, landscaping
    "lighting", // Solar lights, street lighting, path lighting
    "other", // Custom/bespoke products
];
// Product Statuses (lifecycle states for DPP)
export const ProductStatuses = [
    "ACTIVE",
    "MAINTENANCE",
    "DECOMMISSIONED",
    "PENDING_INSTALLATION",
    "REPAIR_NEEDED",
];
// Product Condition Ratings
export const ConditionRatings = ["EXCELLENT", "GOOD", "FAIR", "POOR"];
// Legacy: ESF Divisions (kept for backwards compatibility, prefer ProductCategories)
export const EsfDivisions = [
    "ESFStreet",
    "ESFTheme",
    "ESFSmart",
    "ESFSecure",
];
// User Roles
export const UserRoles = [
    "SuperAdmin", // Platform-wide admin (ESF staff) - cross-org access
    "OrgAdmin", // Organisation admin - manages their org's users and settings
    "AssetManager", // Manages assets/products within their org
    "FieldTech", // Field technician - updates assets, completes work orders
    "Analyst", // Read-only analytics and reporting access
    "PublicViewer", // Public/limited access
];
// Work Order Types
export const WorkOrderTypes = [
    "INSPECTION",
    "MAINTENANCE",
    "REPAIR",
    "INSTALLATION",
    "DECOMMISSION",
    "SENSOR_REPLACEMENT",
];
// Work Order Statuses
export const WorkOrderStatuses = [
    "PENDING",
    "SCHEDULED",
    "IN_PROGRESS",
    "COMPLETED",
    "CANCELLED",
    "ON_HOLD",
];
// Priority Levels
export const Priorities = ["LOW", "MEDIUM", "HIGH", "URGENT"];
// IoT Device Types
export const IoTDeviceTypes = [
    "OCCUPANCY_SENSOR",
    "SOLAR_MONITOR",
    "WASTE_LEVEL",
    "AIR_QUALITY",
    "TEMPERATURE",
    "HUMIDITY",
    "NOISE_LEVEL",
    "LIGHT_SENSOR",
    "STRUCTURAL_MONITOR",
];
// IoT Device Statuses
export const IoTDeviceStatuses = ["ONLINE", "OFFLINE", "MAINTENANCE", "ERROR"];
// ESG Metric Periods
export const ESGPeriods = ["DAILY", "WEEKLY", "MONTHLY", "QUARTERLY", "YEARLY"];
// Notification Types
export const NotificationTypes = [
    "SYSTEM",
    "ASSET_ALERT",
    "WORK_ORDER",
    "IOT_ALERT",
    "ESG_REPORT",
    "USER_ACTION",
];
// Notification Priorities
export const NotificationPriorities = ["LOW", "NORMAL", "HIGH", "URGENT"];
// Audit Actions
export const AuditActions = [
    "ASSET_CREATE",
    "ASSET_UPDATE",
    "ASSET_DELETE",
    "ASSET_RESTORE",
    "WORK_ORDER_CREATE",
    "WORK_ORDER_UPDATE",
    "WORK_ORDER_COMPLETE",
    "WORK_ORDER_DELETE",
    "WORK_ORDER_RESTORE",
    "USER_CREATE",
    "USER_UPDATE",
    "USER_DELETE",
    "USER_RESTORE",
    "USER_LOGIN",
    "USER_LOGOUT",
    "IOT_DEVICE_DELETE",
    "IOT_DEVICE_RESTORE",
    "MASTER_PRODUCT_CREATE",
    "MASTER_PRODUCT_UPDATE",
    "MASTER_PRODUCT_DELETE",
    "MASTER_PRODUCT_RESTORE",
    "ORG_CREATE",
    "ORG_UPDATE",
    "ORG_DELETE",
    "ORG_RESTORE",
    "PRODUCT_CREATE",
    "DPP_API_KEY_CREATE",
    "DPP_API_KEY_REVOKE",
    "BATTERY_PASSPORT_CREATE",
    "IOT_READING",
    "ESG_REPORT_GENERATED",
    "SETTINGS_UPDATE",
];
// ============================================================================
// DPP COMPLIANCE CONSTANTS (EU ESPR / Digital Product Passport)
// ============================================================================
// DPP Access Tiers (ESPR data segregation levels)
export const DppAccessTiers = [
    "PUBLIC", // Consumers, general public - basic sustainability info
    "LEGITIMATE_INTEREST", // Repairers, recyclers, market surveillance - technical details
    "RESTRICTED", // Authorities, notified bodies - full compliance data
];
// Substance of Concern Categories (ESPR Article 7)
export const SubstanceCategories = [
    "SVHC", // Substances of Very High Concern (REACH candidate list)
    "CLP_HAZARDOUS", // Classification, Labelling, Packaging hazardous substances
    "CIRCULARITY_INHIBITOR", // Substances that inhibit reuse/recycling
    "POP", // Persistent Organic Pollutants
];
// Economic Operator Roles (EU Market Actors)
export const EconomicOperatorRoles = [
    "MANUFACTURER",
    "AUTHORIZED_REPRESENTATIVE",
    "IMPORTER",
    "DISTRIBUTOR",
    "FULFILLMENT_SERVICE_PROVIDER",
];
// Carbon Footprint Lifecycle Phases (ISO 14067 / PEF)
export const CarbonLifecyclePhases = [
    "RAW_MATERIAL_ACQUISITION",
    "MANUFACTURING",
    "DISTRIBUTION",
    "USE_PHASE",
    "END_OF_LIFE",
];
// Battery Chemistry Types (EU Battery Regulation 2023/1542)
export const BatteryChemistries = [
    "LITHIUM_ION",
    "LITHIUM_IRON_PHOSPHATE",
    "NICKEL_METAL_HYDRIDE",
    "LEAD_ACID",
    "SODIUM_ION",
    "SOLID_STATE",
    "OTHER",
];
// Battery Categories (EU Battery Regulation)
export const BatteryCategories = [
    "PORTABLE",
    "INDUSTRIAL",
    "ELECTRIC_VEHICLE",
    "LIGHT_MEANS_OF_TRANSPORT",
    "STARTING_LIGHTING_IGNITION",
];
// Certification Types
export const CertificationTypes = [
    "CE_MARKING",
    "ISO_14001",
    "ISO_9001",
    "ISO_14067",
    "EPD", // Environmental Product Declaration
    "FSC", // Forest Stewardship Council
    "PEFC", // Programme for Endorsement of Forest Certification
    "CRADLE_TO_CRADLE",
    "EU_ECOLABEL",
    "BLUE_ANGEL",
    "REACH_COMPLIANT",
    "ROHS_COMPLIANT",
    "OTHER",
];
// ============================================================================
// DPP SCHEMA FIELD CONSTANTS (EU DPP Regulation Compliance)
// ============================================================================
// Product Identifier Types (DPP)
export const ProductIdentifierTypes = [
    "GTIN",
    "PROPRIETARY_PRODUCT_CODE",
    "MODEL_NUMBER",
    "TYPE_CODE",
    "OTHER",
];
// DPP Product Types (Street Furniture)
export const DppProductTypes = [
    "BENCH",
    "LITTER_BIN",
    "RECYCLING_BIN",
    "PICNIC_TABLE",
    "BUS_SHELTER",
    "QUEUE_BARRIER",
    "PLANTER",
    "BOLLARD",
    "SIGNAGE",
    "OTHER",
];
// EU Product Categories (Regulatory Groups)
export const ProductCategoriesEU = [
    "FURNITURE",
    "CONSTRUCTION_PRODUCT",
    "ELECTRICAL_ELECTRONIC",
    "OTHER_PHYSICAL_GOOD",
];
// Economic Operator ID Types
export const EconomicOperatorIdTypes = [
    "VAT_NUMBER",
    "NATIONAL_ID",
    "EORI",
    "OTHER",
];
// Data Carrier Types (QR/RFID/etc)
export const DataCarrierTypes = [
    "QR_CODE",
    "DATA_MATRIX_2D",
    "BARCODE_1D",
    "RFID_NFC",
    "PRINTED_URL",
    "OTHER",
];
// Data Carrier Locations
export const DataCarrierLocations = [
    "ON_PRODUCT",
    "ON_PACKAGING",
    "ON_DOCUMENTATION",
    "MULTIPLE_LOCATIONS",
];
// Data Access Levels
export const DataAccessLevels = [
    "PUBLIC_ONLY",
    "PUBLIC_PLUS_PROFESSIONAL",
    "PUBLIC_PLUS_B2B",
];
// Data Source Types
export const DataSourceTypes = [
    "MANUFACTURER_SYSTEM",
    "IMPORTER_SYSTEM",
    "THIRD_PARTY_SERVICE",
    "MIXED_SOURCES",
];
// Data Update Policies
export const DataUpdatePolicies = [
    "STATIC",
    "PERIODIC_UPDATES",
    "EVENT_BASED_UPDATES",
    "REAL_TIME_SYNC",
];
// Data Retention Periods
export const DataRetentionPeriods = [
    "PRODUCT_LIFETIME",
    "FIXED_YEARS",
    "UNTIL_EOL_DECLARED",
    "OTHER",
];
// Personal Data Inclusion
export const PersonalDataOptions = [
    "NONE",
    "MINIMAL_WITH_CONSENT",
    "NOT_APPLICABLE",
];
// Product Use Context
export const ProductUseContexts = [
    "PUBLIC_STREET_PARK",
    "SCHOOL_UNIVERSITY",
    "TRANSPORT_HUB",
    "THEME_PARK",
    "INDOOR_ATTRACTION",
    "RETAIL_LEISURE",
    "MIXED_USE",
];
// Environment Types
export const EnvironmentTypes = [
    "OUTDOOR_UNCOVERED",
    "OUTDOOR_PARTIALLY_COVERED",
    "INDOOR_PUBLIC",
    "INDOOR_CONTROLLED",
];
// User Group Types
export const UserGroupTypes = [
    "GENERAL_PUBLIC",
    "CHILDREN",
    "ADULTS",
    "MIXED_AGES",
    "REDUCED_MOBILITY_USERS",
    "STAFF_ONLY",
];
// Use Severity Classes
export const UseSeverityClasses = [
    "LIGHT_DUTY",
    "STANDARD_DUTY",
    "HEAVY_DUTY",
    "EXTREME_VANDAL_PRONE",
];
// Load Ratings
export const LoadRatings = [
    "STANDARD_PUBLIC_SEATING",
    "HIGH_LOAD_SEATING",
    "STANDING_LEANING_SUPPORT",
    "STRUCTURAL_SHELTER",
    "NOT_LOAD_BEARING",
];
// Impact / Vandal Classes
export const ImpactVandalClasses = [
    "NONE_DECLARED",
    "BASIC",
    "ANTI_VANDAL",
    "HIGH_ANTI_VANDAL",
    "SECURITY_GRADE",
];
// Fire Performance Classes
export const FirePerformanceClasses = [
    "NOT_APPLICABLE",
    "CLASS_A",
    "CLASS_B",
    "CLASS_C",
    "CLASS_D",
    "CLASS_E",
    "OTHER_SEE_DOCS",
];
// Safety Standards Applied
export const SafetyStandards = [
    "NONE",
    "EN_1176_PLAY",
    "EN_13200_SPECTATOR",
    "LOCAL_THEME_PARK_STANDARD",
    "OTHER",
];
// Intended Use Types
export const IntendedUseTypes = [
    "SEATING",
    "WASTE_COLLECTION",
    "SHELTER",
    "QUEUE_MANAGEMENT",
    "WAYFINDING",
    "MIXED",
    "OTHER",
];
// Accessibility Design Options
export const AccessibilityDesigns = [
    "NOT_SPECIFICALLY_ACCESSIBLE",
    "PARTIALLY_ACCESSIBLE",
    "FULLY_ACCESSIBLE",
];
// Wheelchair Interface Options
export const WheelchairInterfaces = [
    "NOT_SUITABLE",
    "PARTIALLY_SUITABLE",
    "FULLY_SUITABLE",
    "NOT_APPLICABLE",
];
// Armrest Options
export const ArmrestOptions = [
    "NONE",
    "ARMRESTS_BOTH_SIDES",
    "ARMRESTS_SOME_SEATS",
    "BACKREST_AND_ARMRESTS",
    "NOT_APPLICABLE",
];
// Height Categories
export const HeightCategories = [
    "LOW",
    "STANDARD",
    "HIGH",
    "ADJUSTABLE",
    "NOT_APPLICABLE",
];
// Tactile / Visual Cues
export const TactileVisualCues = [
    "NONE",
    "HIGH_CONTRAST_EDGES",
    "TACTILE_SURFACES",
    "BOTH_TACTILE_AND_HIGH_CONTRAST",
];
// Primary Material Groups
export const PrimaryMaterialGroups = [
    "WOOD",
    "METAL_STEEL",
    "METAL_ALUMINIUM",
    "PLASTIC",
    "COMPOSITE",
    "CONCRETE_STONE",
    "MIXED",
];
// Certified Wood Sources
export const CertifiedWoodSources = [
    "NOT_APPLICABLE",
    "FSC_CERTIFIED",
    "PEFC_CERTIFIED",
    "OTHER_CERTIFIED",
    "UNCERTIFIED",
];
// Recycled Content Bands
export const RecycledContentBands = [
    "0_TO_9_PERCENT",
    "10_TO_29_PERCENT",
    "30_TO_49_PERCENT",
    "50_TO_79_PERCENT",
    "80_PLUS_PERCENT",
    "NOT_DECLARED",
];
// Hazardous Substance Flags
export const HazardousSubstanceFlags = [
    "NONE",
    "CONTAINS_RESTRICTED_SEE_DOCS",
    "UNKNOWN",
];
// Corrosion Protection Classes
export const CorrosionProtectionClasses = [
    "NOT_APPLICABLE",
    "LOW",
    "MEDIUM",
    "HIGH_COASTAL_INDUSTRIAL",
];
// UV / Weather Resistance
export const UvWeatherResistances = [
    "NOT_APPLICABLE",
    "INDOOR_ONLY",
    "OUTDOOR_STANDARD",
    "OUTDOOR_HIGH_UV",
    "MARINE_COASTAL",
];
// Surface Treatments
export const SurfaceTreatments = [
    "NONE",
    "PAINT",
    "POWDER_COAT",
    "GALVANISED",
    "ANODISED",
    "WOOD_PRESERVATIVE",
    "ANTI_GRAFFITI_COATING",
    "OTHER",
];
// EPD / LCA Reference Types
export const EpdLcaReferences = [
    "NONE",
    "EPD_AVAILABLE",
    "OTHER_LCA_AVAILABLE",
];
// Carbon Footprint Scopes
export const CarbonFootprintScopes = [
    "PRODUCT_STAGE_ONLY",
    "CRADLE_TO_GATE",
    "CRADLE_TO_GRAVE",
    "CRADLE_TO_GRAVE_INCL_B2B_USE",
    "NOT_DECLARED",
];
// Carbon Footprint Value Bands
export const CarbonFootprintValueBands = [
    "VERY_LOW",
    "LOW",
    "MEDIUM",
    "HIGH",
    "VERY_HIGH",
    "NOT_DECLARED",
];
// Mounting Methods
export const MountingMethods = [
    "FREESTANDING",
    "SURFACE_FIXED_BOLTED",
    "SUB_SURFACE_ANCHORS_FOUNDATIONS",
    "WALL_MOUNTED",
    "INTEGRATED_STRUCTURE",
];
// Typical Installation Settings
export const InstallationSettings = [
    "PAVEMENT",
    "SOFT_LANDSCAPING",
    "PLATFORM",
    "INDOOR_FLOOR",
    "WALL",
    "OTHER",
];
// Inspection Regimes
export const InspectionRegimes = [
    "WEEKLY",
    "MONTHLY",
    "QUARTERLY",
    "ANNUALLY",
    "PER_LOCAL_RISK_ASSESSMENT",
];
// Cleaning Frequencies
export const CleaningFrequencies = [
    "DAILY",
    "SEVERAL_TIMES_PER_WEEK",
    "WEEKLY",
    "AS_NEEDED",
];
// Approved Cleaning Agents
export const ApprovedCleaningAgents = [
    "WATER_ONLY",
    "NEUTRAL_DETERGENT",
    "SOLVENT_BASED_ALLOWED",
    "SPECIAL_GRAFFITI_REMOVER_ONLY",
    "SEE_INSTRUCTIONS",
];
// Serviceable Component Types
export const ServiceableComponentTypes = [
    "NONE",
    "SEATING_SLATS",
    "PANELS_GLAZING",
    "FIXINGS_ANCHORS",
    "BIN_LINER_INNER",
    "SIGN_PANEL",
    "OTHER",
];
// Spare Parts Kit IDs
export const SparePartsKitIds = [
    "NONE",
    "STANDARD_KIT",
    "ANTI_VANDAL_KIT",
    "THEME_PARK_CUSTOM_KIT",
];
// Expected Service Life Bands (Outdoor)
export const ExpectedServiceLifeBands = [
    "LESS_THAN_5_YEARS",
    "5_TO_9_YEARS",
    "10_TO_14_YEARS",
    "15_TO_24_YEARS",
    "25_PLUS_YEARS",
    "NOT_DECLARED",
];
// Repairability Ratings
export const RepairabilityRatings = [
    "NOT_REPAIRABLE",
    "LIMITED_REPAIR",
    "REPAIRABLE_BASIC_TOOLS",
    "MODULAR_FULLY_REPAIRABLE",
];
// End of Life Routes
export const EndOfLifeRoutes = [
    "LANDFILL_INCINERATION",
    "ENERGY_RECOVERY",
    "MATERIAL_RECYCLING",
    "COMPONENT_REUSE",
    "REFURBISH_REPURPOSE",
];
// Take Back / Producer Schemes
export const TakeBackSchemes = [
    "NONE",
    "VOLUNTARY_TAKE_BACK",
    "CONTRACTED_TAKE_BACK_B2B",
    "THIRD_PARTY_SCHEME",
];
// Legal / Conformity Markings
export const ConformityMarkings = [
    "NOT_DECLARED",
    "CE_UKCA_OTHER_APPLICABLE",
    "IN_CONFORMITY_WITH_EU_LAW",
];
// EU Legislation References
export const EuLegislationReferences = [
    "ESPR_2024_1781",
    "CONSTRUCTION_PRODUCTS_RULES",
    "GENERAL_PRODUCT_SAFETY",
    "OTHER_SEE_DOCS",
];
// GPP / Internal Criteria Met
export const GppCriteriaOptions = [
    "NONE_DECLARED",
    "MEETS_EU_GPP_CORE",
    "MEETS_EU_GPP_COMPREHENSIVE",
    "MEETS_BUYER_SPECIFIC_CRITERIA",
];
// DPP Document Types
export const DppDocumentTypes = [
    "DECLARATION_OF_CONFORMITY",
    "TECHNICAL_FILE",
    "USER_MANUAL",
    "REPAIR_MANUAL",
    "DISASSEMBLY_INSTRUCTIONS",
    "MATERIAL_SAFETY_DATA_SHEET",
    "EPD_CERTIFICATE",
    "THIRD_PARTY_VERIFICATION",
    "DUE_DILIGENCE_REPORT",
    "CHAIN_OF_CUSTODY",
    "OTHER",
];
// Spare Part Availability Status
export const SparePartAvailabilities = [
    "AVAILABLE",
    "LIMITED_STOCK",
    "BACK_ORDER",
    "DISCONTINUED",
    "MADE_TO_ORDER",
];
// Due Diligence Status
export const DueDiligenceStatuses = [
    "VERIFIED",
    "PENDING",
    "NOT_VERIFIED",
    "FLAGGED",
];
// DPP Compliance Status
export const DppComplianceStatuses = [
    "DRAFT",
    "PENDING_REVIEW",
    "COMPLIANT",
    "NON_COMPLIANT",
    "EXPIRED",
];
// Carbon Intensity Classes (EU Battery Regulation)
export const CarbonIntensityClasses = ["A", "B", "C", "D", "E"];
export const AssetCategories = ProductCategories;
export const AssetStatuses = ProductStatuses;
/**
 * Countries - Full list of countries (ISO 3166)
 * Sorted alphabetically by name
 */
export const Countries = [
    { name: "Afghanistan", cca2: "AF", cca3: "AFG", flag: "\u{1F1E6}\u{1F1EB}" },
    { name: "Albania", cca2: "AL", cca3: "ALB", flag: "\u{1F1E6}\u{1F1F1}" },
    { name: "Algeria", cca2: "DZ", cca3: "DZA", flag: "\u{1F1E9}\u{1F1FF}" },
    { name: "Andorra", cca2: "AD", cca3: "AND", flag: "\u{1F1E6}\u{1F1E9}" },
    { name: "Angola", cca2: "AO", cca3: "AGO", flag: "\u{1F1E6}\u{1F1F4}" },
    { name: "Antigua and Barbuda", cca2: "AG", cca3: "ATG", flag: "\u{1F1E6}\u{1F1EC}" },
    { name: "Argentina", cca2: "AR", cca3: "ARG", flag: "\u{1F1E6}\u{1F1F7}" },
    { name: "Armenia", cca2: "AM", cca3: "ARM", flag: "\u{1F1E6}\u{1F1F2}" },
    { name: "Australia", cca2: "AU", cca3: "AUS", flag: "\u{1F1E6}\u{1F1FA}" },
    { name: "Austria", cca2: "AT", cca3: "AUT", flag: "\u{1F1E6}\u{1F1F9}" },
    { name: "Azerbaijan", cca2: "AZ", cca3: "AZE", flag: "\u{1F1E6}\u{1F1FF}" },
    { name: "Bahamas", cca2: "BS", cca3: "BHS", flag: "\u{1F1E7}\u{1F1F8}" },
    { name: "Bahrain", cca2: "BH", cca3: "BHR", flag: "\u{1F1E7}\u{1F1ED}" },
    { name: "Bangladesh", cca2: "BD", cca3: "BGD", flag: "\u{1F1E7}\u{1F1E9}" },
    { name: "Barbados", cca2: "BB", cca3: "BRB", flag: "\u{1F1E7}\u{1F1E7}" },
    { name: "Belarus", cca2: "BY", cca3: "BLR", flag: "\u{1F1E7}\u{1F1FE}" },
    { name: "Belgium", cca2: "BE", cca3: "BEL", flag: "\u{1F1E7}\u{1F1EA}" },
    { name: "Belize", cca2: "BZ", cca3: "BLZ", flag: "\u{1F1E7}\u{1F1FF}" },
    { name: "Benin", cca2: "BJ", cca3: "BEN", flag: "\u{1F1E7}\u{1F1EF}" },
    { name: "Bhutan", cca2: "BT", cca3: "BTN", flag: "\u{1F1E7}\u{1F1F9}" },
    { name: "Bolivia", cca2: "BO", cca3: "BOL", flag: "\u{1F1E7}\u{1F1F4}" },
    { name: "Bosnia and Herzegovina", cca2: "BA", cca3: "BIH", flag: "\u{1F1E7}\u{1F1E6}" },
    { name: "Botswana", cca2: "BW", cca3: "BWA", flag: "\u{1F1E7}\u{1F1FC}" },
    { name: "Brazil", cca2: "BR", cca3: "BRA", flag: "\u{1F1E7}\u{1F1F7}" },
    { name: "Brunei", cca2: "BN", cca3: "BRN", flag: "\u{1F1E7}\u{1F1F3}" },
    { name: "Bulgaria", cca2: "BG", cca3: "BGR", flag: "\u{1F1E7}\u{1F1EC}" },
    { name: "Burkina Faso", cca2: "BF", cca3: "BFA", flag: "\u{1F1E7}\u{1F1EB}" },
    { name: "Burundi", cca2: "BI", cca3: "BDI", flag: "\u{1F1E7}\u{1F1EE}" },
    { name: "Cambodia", cca2: "KH", cca3: "KHM", flag: "\u{1F1F0}\u{1F1ED}" },
    { name: "Cameroon", cca2: "CM", cca3: "CMR", flag: "\u{1F1E8}\u{1F1F2}" },
    { name: "Canada", cca2: "CA", cca3: "CAN", flag: "\u{1F1E8}\u{1F1E6}" },
    { name: "Cape Verde", cca2: "CV", cca3: "CPV", flag: "\u{1F1E8}\u{1F1FB}" },
    { name: "Central African Republic", cca2: "CF", cca3: "CAF", flag: "\u{1F1E8}\u{1F1EB}" },
    { name: "Chad", cca2: "TD", cca3: "TCD", flag: "\u{1F1F9}\u{1F1E9}" },
    { name: "Chile", cca2: "CL", cca3: "CHL", flag: "\u{1F1E8}\u{1F1F1}" },
    { name: "China", cca2: "CN", cca3: "CHN", flag: "\u{1F1E8}\u{1F1F3}" },
    { name: "Colombia", cca2: "CO", cca3: "COL", flag: "\u{1F1E8}\u{1F1F4}" },
    { name: "Comoros", cca2: "KM", cca3: "COM", flag: "\u{1F1F0}\u{1F1F2}" },
    { name: "Costa Rica", cca2: "CR", cca3: "CRI", flag: "\u{1F1E8}\u{1F1F7}" },
    { name: "Croatia", cca2: "HR", cca3: "HRV", flag: "\u{1F1ED}\u{1F1F7}" },
    { name: "Cuba", cca2: "CU", cca3: "CUB", flag: "\u{1F1E8}\u{1F1FA}" },
    { name: "Cyprus", cca2: "CY", cca3: "CYP", flag: "\u{1F1E8}\u{1F1FE}" },
    { name: "Czechia", cca2: "CZ", cca3: "CZE", flag: "\u{1F1E8}\u{1F1FF}" },
    { name: "Denmark", cca2: "DK", cca3: "DNK", flag: "\u{1F1E9}\u{1F1F0}" },
    { name: "Djibouti", cca2: "DJ", cca3: "DJI", flag: "\u{1F1E9}\u{1F1EF}" },
    { name: "Dominica", cca2: "DM", cca3: "DMA", flag: "\u{1F1E9}\u{1F1F2}" },
    { name: "Dominican Republic", cca2: "DO", cca3: "DOM", flag: "\u{1F1E9}\u{1F1F4}" },
    { name: "DR Congo", cca2: "CD", cca3: "COD", flag: "\u{1F1E8}\u{1F1E9}" },
    { name: "Ecuador", cca2: "EC", cca3: "ECU", flag: "\u{1F1EA}\u{1F1E8}" },
    { name: "Egypt", cca2: "EG", cca3: "EGY", flag: "\u{1F1EA}\u{1F1EC}" },
    { name: "El Salvador", cca2: "SV", cca3: "SLV", flag: "\u{1F1F8}\u{1F1FB}" },
    { name: "Equatorial Guinea", cca2: "GQ", cca3: "GNQ", flag: "\u{1F1EC}\u{1F1F6}" },
    { name: "Eritrea", cca2: "ER", cca3: "ERI", flag: "\u{1F1EA}\u{1F1F7}" },
    { name: "Estonia", cca2: "EE", cca3: "EST", flag: "\u{1F1EA}\u{1F1EA}" },
    { name: "Eswatini", cca2: "SZ", cca3: "SWZ", flag: "\u{1F1F8}\u{1F1FF}" },
    { name: "Ethiopia", cca2: "ET", cca3: "ETH", flag: "\u{1F1EA}\u{1F1F9}" },
    { name: "Fiji", cca2: "FJ", cca3: "FJI", flag: "\u{1F1EB}\u{1F1EF}" },
    { name: "Finland", cca2: "FI", cca3: "FIN", flag: "\u{1F1EB}\u{1F1EE}" },
    { name: "France", cca2: "FR", cca3: "FRA", flag: "\u{1F1EB}\u{1F1F7}" },
    { name: "Gabon", cca2: "GA", cca3: "GAB", flag: "\u{1F1EC}\u{1F1E6}" },
    { name: "Gambia", cca2: "GM", cca3: "GMB", flag: "\u{1F1EC}\u{1F1F2}" },
    { name: "Georgia", cca2: "GE", cca3: "GEO", flag: "\u{1F1EC}\u{1F1EA}" },
    { name: "Germany", cca2: "DE", cca3: "DEU", flag: "\u{1F1E9}\u{1F1EA}" },
    { name: "Ghana", cca2: "GH", cca3: "GHA", flag: "\u{1F1EC}\u{1F1ED}" },
    { name: "Greece", cca2: "GR", cca3: "GRC", flag: "\u{1F1EC}\u{1F1F7}" },
    { name: "Grenada", cca2: "GD", cca3: "GRD", flag: "\u{1F1EC}\u{1F1E9}" },
    { name: "Guatemala", cca2: "GT", cca3: "GTM", flag: "\u{1F1EC}\u{1F1F9}" },
    { name: "Guinea", cca2: "GN", cca3: "GIN", flag: "\u{1F1EC}\u{1F1F3}" },
    { name: "Guinea-Bissau", cca2: "GW", cca3: "GNB", flag: "\u{1F1EC}\u{1F1FC}" },
    { name: "Guyana", cca2: "GY", cca3: "GUY", flag: "\u{1F1EC}\u{1F1FE}" },
    { name: "Haiti", cca2: "HT", cca3: "HTI", flag: "\u{1F1ED}\u{1F1F9}" },
    { name: "Honduras", cca2: "HN", cca3: "HND", flag: "\u{1F1ED}\u{1F1F3}" },
    { name: "Hungary", cca2: "HU", cca3: "HUN", flag: "\u{1F1ED}\u{1F1FA}" },
    { name: "Iceland", cca2: "IS", cca3: "ISL", flag: "\u{1F1EE}\u{1F1F8}" },
    { name: "India", cca2: "IN", cca3: "IND", flag: "\u{1F1EE}\u{1F1F3}" },
    { name: "Indonesia", cca2: "ID", cca3: "IDN", flag: "\u{1F1EE}\u{1F1E9}" },
    { name: "Iran", cca2: "IR", cca3: "IRN", flag: "\u{1F1EE}\u{1F1F7}" },
    { name: "Iraq", cca2: "IQ", cca3: "IRQ", flag: "\u{1F1EE}\u{1F1F6}" },
    { name: "Ireland", cca2: "IE", cca3: "IRL", flag: "\u{1F1EE}\u{1F1EA}" },
    { name: "Israel", cca2: "IL", cca3: "ISR", flag: "\u{1F1EE}\u{1F1F1}" },
    { name: "Italy", cca2: "IT", cca3: "ITA", flag: "\u{1F1EE}\u{1F1F9}" },
    { name: "Ivory Coast", cca2: "CI", cca3: "CIV", flag: "\u{1F1E8}\u{1F1EE}" },
    { name: "Jamaica", cca2: "JM", cca3: "JAM", flag: "\u{1F1EF}\u{1F1F2}" },
    { name: "Japan", cca2: "JP", cca3: "JPN", flag: "\u{1F1EF}\u{1F1F5}" },
    { name: "Jordan", cca2: "JO", cca3: "JOR", flag: "\u{1F1EF}\u{1F1F4}" },
    { name: "Kazakhstan", cca2: "KZ", cca3: "KAZ", flag: "\u{1F1F0}\u{1F1FF}" },
    { name: "Kenya", cca2: "KE", cca3: "KEN", flag: "\u{1F1F0}\u{1F1EA}" },
    { name: "Kiribati", cca2: "KI", cca3: "KIR", flag: "\u{1F1F0}\u{1F1EE}" },
    { name: "Kuwait", cca2: "KW", cca3: "KWT", flag: "\u{1F1F0}\u{1F1FC}" },
    { name: "Kyrgyzstan", cca2: "KG", cca3: "KGZ", flag: "\u{1F1F0}\u{1F1EC}" },
    { name: "Laos", cca2: "LA", cca3: "LAO", flag: "\u{1F1F1}\u{1F1E6}" },
    { name: "Latvia", cca2: "LV", cca3: "LVA", flag: "\u{1F1F1}\u{1F1FB}" },
    { name: "Lebanon", cca2: "LB", cca3: "LBN", flag: "\u{1F1F1}\u{1F1E7}" },
    { name: "Lesotho", cca2: "LS", cca3: "LSO", flag: "\u{1F1F1}\u{1F1F8}" },
    { name: "Liberia", cca2: "LR", cca3: "LBR", flag: "\u{1F1F1}\u{1F1F7}" },
    { name: "Libya", cca2: "LY", cca3: "LBY", flag: "\u{1F1F1}\u{1F1FE}" },
    { name: "Liechtenstein", cca2: "LI", cca3: "LIE", flag: "\u{1F1F1}\u{1F1EE}" },
    { name: "Lithuania", cca2: "LT", cca3: "LTU", flag: "\u{1F1F1}\u{1F1F9}" },
    { name: "Luxembourg", cca2: "LU", cca3: "LUX", flag: "\u{1F1F1}\u{1F1FA}" },
    { name: "Madagascar", cca2: "MG", cca3: "MDG", flag: "\u{1F1F2}\u{1F1EC}" },
    { name: "Malawi", cca2: "MW", cca3: "MWI", flag: "\u{1F1F2}\u{1F1FC}" },
    { name: "Malaysia", cca2: "MY", cca3: "MYS", flag: "\u{1F1F2}\u{1F1FE}" },
    { name: "Maldives", cca2: "MV", cca3: "MDV", flag: "\u{1F1F2}\u{1F1FB}" },
    { name: "Mali", cca2: "ML", cca3: "MLI", flag: "\u{1F1F2}\u{1F1F1}" },
    { name: "Malta", cca2: "MT", cca3: "MLT", flag: "\u{1F1F2}\u{1F1F9}" },
    { name: "Marshall Islands", cca2: "MH", cca3: "MHL", flag: "\u{1F1F2}\u{1F1ED}" },
    { name: "Mauritania", cca2: "MR", cca3: "MRT", flag: "\u{1F1F2}\u{1F1F7}" },
    { name: "Mauritius", cca2: "MU", cca3: "MUS", flag: "\u{1F1F2}\u{1F1FA}" },
    { name: "Mexico", cca2: "MX", cca3: "MEX", flag: "\u{1F1F2}\u{1F1FD}" },
    { name: "Micronesia", cca2: "FM", cca3: "FSM", flag: "\u{1F1EB}\u{1F1F2}" },
    { name: "Moldova", cca2: "MD", cca3: "MDA", flag: "\u{1F1F2}\u{1F1E9}" },
    { name: "Monaco", cca2: "MC", cca3: "MCO", flag: "\u{1F1F2}\u{1F1E8}" },
    { name: "Mongolia", cca2: "MN", cca3: "MNG", flag: "\u{1F1F2}\u{1F1F3}" },
    { name: "Montenegro", cca2: "ME", cca3: "MNE", flag: "\u{1F1F2}\u{1F1EA}" },
    { name: "Morocco", cca2: "MA", cca3: "MAR", flag: "\u{1F1F2}\u{1F1E6}" },
    { name: "Mozambique", cca2: "MZ", cca3: "MOZ", flag: "\u{1F1F2}\u{1F1FF}" },
    { name: "Myanmar", cca2: "MM", cca3: "MMR", flag: "\u{1F1F2}\u{1F1F2}" },
    { name: "Namibia", cca2: "NA", cca3: "NAM", flag: "\u{1F1F3}\u{1F1E6}" },
    { name: "Nauru", cca2: "NR", cca3: "NRU", flag: "\u{1F1F3}\u{1F1F7}" },
    { name: "Nepal", cca2: "NP", cca3: "NPL", flag: "\u{1F1F3}\u{1F1F5}" },
    { name: "Netherlands", cca2: "NL", cca3: "NLD", flag: "\u{1F1F3}\u{1F1F1}" },
    { name: "New Zealand", cca2: "NZ", cca3: "NZL", flag: "\u{1F1F3}\u{1F1FF}" },
    { name: "Nicaragua", cca2: "NI", cca3: "NIC", flag: "\u{1F1F3}\u{1F1EE}" },
    { name: "Niger", cca2: "NE", cca3: "NER", flag: "\u{1F1F3}\u{1F1EA}" },
    { name: "Nigeria", cca2: "NG", cca3: "NGA", flag: "\u{1F1F3}\u{1F1EC}" },
    { name: "North Korea", cca2: "KP", cca3: "PRK", flag: "\u{1F1F0}\u{1F1F5}" },
    { name: "North Macedonia", cca2: "MK", cca3: "MKD", flag: "\u{1F1F2}\u{1F1F0}" },
    { name: "Norway", cca2: "NO", cca3: "NOR", flag: "\u{1F1F3}\u{1F1F4}" },
    { name: "Oman", cca2: "OM", cca3: "OMN", flag: "\u{1F1F4}\u{1F1F2}" },
    { name: "Pakistan", cca2: "PK", cca3: "PAK", flag: "\u{1F1F5}\u{1F1F0}" },
    { name: "Palau", cca2: "PW", cca3: "PLW", flag: "\u{1F1F5}\u{1F1FC}" },
    { name: "Palestine", cca2: "PS", cca3: "PSE", flag: "\u{1F1F5}\u{1F1F8}" },
    { name: "Panama", cca2: "PA", cca3: "PAN", flag: "\u{1F1F5}\u{1F1E6}" },
    { name: "Papua New Guinea", cca2: "PG", cca3: "PNG", flag: "\u{1F1F5}\u{1F1EC}" },
    { name: "Paraguay", cca2: "PY", cca3: "PRY", flag: "\u{1F1F5}\u{1F1FE}" },
    { name: "Peru", cca2: "PE", cca3: "PER", flag: "\u{1F1F5}\u{1F1EA}" },
    { name: "Philippines", cca2: "PH", cca3: "PHL", flag: "\u{1F1F5}\u{1F1ED}" },
    { name: "Poland", cca2: "PL", cca3: "POL", flag: "\u{1F1F5}\u{1F1F1}" },
    { name: "Portugal", cca2: "PT", cca3: "PRT", flag: "\u{1F1F5}\u{1F1F9}" },
    { name: "Qatar", cca2: "QA", cca3: "QAT", flag: "\u{1F1F6}\u{1F1E6}" },
    { name: "Republic of the Congo", cca2: "CG", cca3: "COG", flag: "\u{1F1E8}\u{1F1EC}" },
    { name: "Romania", cca2: "RO", cca3: "ROU", flag: "\u{1F1F7}\u{1F1F4}" },
    { name: "Russia", cca2: "RU", cca3: "RUS", flag: "\u{1F1F7}\u{1F1FA}" },
    { name: "Rwanda", cca2: "RW", cca3: "RWA", flag: "\u{1F1F7}\u{1F1FC}" },
    { name: "Saint Kitts and Nevis", cca2: "KN", cca3: "KNA", flag: "\u{1F1F0}\u{1F1F3}" },
    { name: "Saint Lucia", cca2: "LC", cca3: "LCA", flag: "\u{1F1F1}\u{1F1E8}" },
    { name: "Saint Vincent and the Grenadines", cca2: "VC", cca3: "VCT", flag: "\u{1F1FB}\u{1F1E8}" },
    { name: "Samoa", cca2: "WS", cca3: "WSM", flag: "\u{1F1FC}\u{1F1F8}" },
    { name: "San Marino", cca2: "SM", cca3: "SMR", flag: "\u{1F1F8}\u{1F1F2}" },
    { name: "Saudi Arabia", cca2: "SA", cca3: "SAU", flag: "\u{1F1F8}\u{1F1E6}" },
    { name: "Senegal", cca2: "SN", cca3: "SEN", flag: "\u{1F1F8}\u{1F1F3}" },
    { name: "Serbia", cca2: "RS", cca3: "SRB", flag: "\u{1F1F7}\u{1F1F8}" },
    { name: "Seychelles", cca2: "SC", cca3: "SYC", flag: "\u{1F1F8}\u{1F1E8}" },
    { name: "Sierra Leone", cca2: "SL", cca3: "SLE", flag: "\u{1F1F8}\u{1F1F1}" },
    { name: "Singapore", cca2: "SG", cca3: "SGP", flag: "\u{1F1F8}\u{1F1EC}" },
    { name: "Slovakia", cca2: "SK", cca3: "SVK", flag: "\u{1F1F8}\u{1F1F0}" },
    { name: "Slovenia", cca2: "SI", cca3: "SVN", flag: "\u{1F1F8}\u{1F1EE}" },
    { name: "Solomon Islands", cca2: "SB", cca3: "SLB", flag: "\u{1F1F8}\u{1F1E7}" },
    { name: "Somalia", cca2: "SO", cca3: "SOM", flag: "\u{1F1F8}\u{1F1F4}" },
    { name: "South Africa", cca2: "ZA", cca3: "ZAF", flag: "\u{1F1FF}\u{1F1E6}" },
    { name: "South Korea", cca2: "KR", cca3: "KOR", flag: "\u{1F1F0}\u{1F1F7}" },
    { name: "South Sudan", cca2: "SS", cca3: "SSD", flag: "\u{1F1F8}\u{1F1F8}" },
    { name: "Spain", cca2: "ES", cca3: "ESP", flag: "\u{1F1EA}\u{1F1F8}" },
    { name: "Sri Lanka", cca2: "LK", cca3: "LKA", flag: "\u{1F1F1}\u{1F1F0}" },
    { name: "Sudan", cca2: "SD", cca3: "SDN", flag: "\u{1F1F8}\u{1F1E9}" },
    { name: "Suriname", cca2: "SR", cca3: "SUR", flag: "\u{1F1F8}\u{1F1F7}" },
    { name: "Sweden", cca2: "SE", cca3: "SWE", flag: "\u{1F1F8}\u{1F1EA}" },
    { name: "Switzerland", cca2: "CH", cca3: "CHE", flag: "\u{1F1E8}\u{1F1ED}" },
    { name: "Syria", cca2: "SY", cca3: "SYR", flag: "\u{1F1F8}\u{1F1FE}" },
    { name: "Taiwan", cca2: "TW", cca3: "TWN", flag: "\u{1F1F9}\u{1F1FC}" },
    { name: "Tajikistan", cca2: "TJ", cca3: "TJK", flag: "\u{1F1F9}\u{1F1EF}" },
    { name: "Tanzania", cca2: "TZ", cca3: "TZA", flag: "\u{1F1F9}\u{1F1FF}" },
    { name: "Thailand", cca2: "TH", cca3: "THA", flag: "\u{1F1F9}\u{1F1ED}" },
    { name: "Timor-Leste", cca2: "TL", cca3: "TLS", flag: "\u{1F1F9}\u{1F1F1}" },
    { name: "Togo", cca2: "TG", cca3: "TGO", flag: "\u{1F1F9}\u{1F1EC}" },
    { name: "Tonga", cca2: "TO", cca3: "TON", flag: "\u{1F1F9}\u{1F1F4}" },
    { name: "Trinidad and Tobago", cca2: "TT", cca3: "TTO", flag: "\u{1F1F9}\u{1F1F9}" },
    { name: "Tunisia", cca2: "TN", cca3: "TUN", flag: "\u{1F1F9}\u{1F1F3}" },
    { name: "Turkey", cca2: "TR", cca3: "TUR", flag: "\u{1F1F9}\u{1F1F7}" },
    { name: "Turkmenistan", cca2: "TM", cca3: "TKM", flag: "\u{1F1F9}\u{1F1F2}" },
    { name: "Tuvalu", cca2: "TV", cca3: "TUV", flag: "\u{1F1F9}\u{1F1FB}" },
    { name: "Uganda", cca2: "UG", cca3: "UGA", flag: "\u{1F1FA}\u{1F1EC}" },
    { name: "Ukraine", cca2: "UA", cca3: "UKR", flag: "\u{1F1FA}\u{1F1E6}" },
    { name: "United Arab Emirates", cca2: "AE", cca3: "ARE", flag: "\u{1F1E6}\u{1F1EA}" },
    { name: "United Kingdom", cca2: "GB", cca3: "GBR", flag: "\u{1F1EC}\u{1F1E7}" },
    { name: "United States", cca2: "US", cca3: "USA", flag: "\u{1F1FA}\u{1F1F8}" },
    { name: "Uruguay", cca2: "UY", cca3: "URY", flag: "\u{1F1FA}\u{1F1FE}" },
    { name: "Uzbekistan", cca2: "UZ", cca3: "UZB", flag: "\u{1F1FA}\u{1F1FF}" },
    { name: "Vanuatu", cca2: "VU", cca3: "VUT", flag: "\u{1F1FB}\u{1F1FA}" },
    { name: "Vatican City", cca2: "VA", cca3: "VAT", flag: "\u{1F1FB}\u{1F1E6}" },
    { name: "Venezuela", cca2: "VE", cca3: "VEN", flag: "\u{1F1FB}\u{1F1EA}" },
    { name: "Vietnam", cca2: "VN", cca3: "VNM", flag: "\u{1F1FB}\u{1F1F3}" },
    { name: "Yemen", cca2: "YE", cca3: "YEM", flag: "\u{1F1FE}\u{1F1EA}" },
    { name: "Zambia", cca2: "ZM", cca3: "ZMB", flag: "\u{1F1FF}\u{1F1F2}" },
    { name: "Zimbabwe", cca2: "ZW", cca3: "ZWE", flag: "\u{1F1FF}\u{1F1FC}" },
];
/**
 * Get country by ISO alpha-2 code
 */
export const getCountryByCca2 = (cca2) => Countries.find(c => c.cca2 === cca2);
/**
 * Get country by ISO alpha-3 code
 */
export const getCountryByCca3 = (cca3) => Countries.find(c => c.cca3 === cca3);
/**
 * Get country by name
 */
export const getCountryByName = (name) => Countries.find(c => c.name.toLowerCase() === name.toLowerCase());
// ============================================================================
// ENTITY INTERFACES
// ============================================================================
/**
 * Organisation - Multi-tenant organisation
 */
// Organisation Tiers (subscription levels)
export const OrganisationTiers = ["free", "standard", "enterprise"];
/**
 * Lifecycle Event Types
 */
export const LifecycleEventTypes = [
    'MANUFACTURE',
    'SHIPMENT',
    'INSTALLATION',
    'INSPECTION',
    'MAINTENANCE',
    'REPAIR',
    'WARRANTY_START',
    'WARRANTY_END',
    'DECOMMISSION',
    'EOL',
];
// ============================================================================
// ZOD SCHEMAS
// ============================================================================
export const GeoLocationSchema = z.object({
    lat: z.number(),
    lng: z.number(),
    address: z.string().optional(),
    city: z.string().optional(),
    postcode: z.string().optional(),
    country: z.string().optional(),
});
export const OrganisationSchema = z.object({
    id: z.string(),
    name: z.string().min(1),
    slug: z.string().min(1),
    contactEmail: z.string().email().optional(),
    contactPhone: z.string().optional(),
    address: z.string().optional(),
    logoUrl: z.string().url().optional(),
    // Multi-tenancy fields
    isEsfOrg: z.boolean().optional(),
    enabled: z.boolean().optional(),
    tier: z.enum(OrganisationTiers).optional(),
    maxUsers: z.number().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().optional(),
});
export const CreateOrganisationSchema = z.object({
    name: z.string().min(1),
    slug: z.string().min(1).regex(/^[a-z0-9-]+$/),
    contactEmail: z.string().email().optional(),
    contactPhone: z.string().optional(),
    address: z.string().optional(),
    logoUrl: z.string().url().optional(),
    // Multi-tenancy fields (SuperAdmin only)
    isEsfOrg: z.boolean().optional(),
    tier: z.enum(OrganisationTiers).optional(),
    maxUsers: z.number().optional(),
});
export const UpdateOrganisationSchema = z.object({
    name: z.string().min(1).optional(),
    slug: z.string().min(1).regex(/^[a-z0-9-]+$/).optional(),
    contactEmail: z.string().email().optional().nullable(),
    contactPhone: z.string().optional().nullable(),
    address: z.string().optional().nullable(),
    logoUrl: z.string().url().optional().nullable(),
    // Multi-tenancy fields (SuperAdmin only for some)
    enabled: z.boolean().optional(),
    tier: z.enum(OrganisationTiers).optional(),
    maxUsers: z.number().optional(),
    deletedAt: z.string().nullable().optional(), // null to restore, string to delete
});
export const UserSchema = z.object({
    email: z.string().email(),
    organisationId: z.string(),
    role: z.enum(UserRoles),
    name: z.string().min(1),
    phoneNumber: z.string().optional(),
    enabled: z.boolean(),
    lastLoginAt: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string().optional(),
    deletedAt: z.string().optional(),
    // Invitation tracking
    invitedBy: z.string().email().optional(),
    invitedAt: z.string().optional(),
});
export const CreateUserSchema = z.object({
    email: z.string().email(),
    role: z.enum(UserRoles),
    name: z.string().min(1),
    phoneNumber: z.string().optional(),
    organisationId: z.string().optional(), // SuperAdmin can specify org
});
export const UpdateUserSchema = z.object({
    role: z.enum(UserRoles).optional(),
    name: z.string().min(1).optional(),
    phoneNumber: z.string().optional().nullable(),
    enabled: z.boolean().optional(),
    deletedAt: z.string().nullable().optional(), // null to restore, string to delete
});
export const ProductSchema = z.object({
    id: z.string(),
    organisationId: z.string(),
    passportId: z.string(),
    name: z.string().min(1),
    category: z.enum(ProductCategories),
    subcategory: z.string().optional(),
    model: z.string().optional(),
    serialNumber: z.string().optional(),
    gtin: z.string().optional(),
    location: GeoLocationSchema.optional(),
    installationSite: z.string().optional(),
    installationDate: z.string().optional(),
    status: z.enum(ProductStatuses),
    condition: z.enum(ConditionRatings).optional(),
    lastInspectionDate: z.string().optional(),
    nextMaintenanceDate: z.string().optional(),
    manufacturer: z.string().optional(),
    manufacturerCountry: z.string().optional(),
    materials: z.array(z.unknown()).optional(),
    certifications: z.array(z.string()).optional(),
    carbonFootprint: z.number().optional(),
    recyclabilityScore: z.number().min(0).max(100).optional(),
    expectedLifespan: z.number().optional(),
    warrantyEndDate: z.string().optional(),
    repairabilityScore: z.number().min(0).max(10).optional(),
    iotDeviceIds: z.array(z.string()).optional(),
    description: z.string().optional(),
    notes: z.string().optional(),
    tags: z.array(z.string()).optional(),
    imageUrls: z.array(z.string()).optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().optional(),
});
export const CreateProductSchema = z.object({
    name: z.string().min(1),
    category: z.enum(ProductCategories),
    subcategory: z.string().optional(),
    model: z.string().optional(),
    serialNumber: z.string().optional(),
    gtin: z.string().optional(),
    location: GeoLocationSchema.optional(),
    installationSite: z.string().optional(),
    installationDate: z.string().optional(),
    status: z.enum(ProductStatuses).default("PENDING_INSTALLATION"),
    condition: z.enum(ConditionRatings).optional(),
    manufacturer: z.string().optional(),
    manufacturerCountry: z.string().optional(),
    materials: z.array(z.unknown()).optional(),
    certifications: z.array(z.string()).optional(),
    carbonFootprint: z.number().optional(),
    recyclabilityScore: z.number().min(0).max(100).optional(),
    expectedLifespan: z.number().optional(),
    warrantyEndDate: z.string().optional(),
    repairabilityScore: z.number().min(0).max(10).optional(),
    description: z.string().optional(),
    notes: z.string().optional(),
    tags: z.array(z.string()).optional(),
    // Master Product Reference (optional - for assets created from templates)
    masterProductId: z.string().optional(),
});
export const UpdateProductSchema = CreateProductSchema.extend({
    deletedAt: z.string().nullable().optional(), // null to restore, string to delete
}).partial().refine((v) => Object.keys(v).length > 0, { message: "At least one field must be provided" });
// Backwards compatibility aliases
export const AssetSchema = ProductSchema;
export const CreateAssetSchema = CreateProductSchema;
export const UpdateAssetSchema = UpdateProductSchema;
// ============================================================================
// MASTER PRODUCT SCHEMAS
// ============================================================================
export const MasterProductSchema = z.object({
    id: z.string(),
    organisationId: z.string(),
    name: z.string().min(1),
    sku: z.string().optional(),
    version: z.number().int().positive(),
    isActive: z.boolean(),
    category: z.enum(ProductCategories),
    subcategory: z.string().optional(),
    model: z.string().optional(),
    gtin: z.string().optional(),
    manufacturer: z.string().optional(),
    manufacturerCountry: z.string().optional(),
    materials: z.array(z.unknown()).optional(),
    certifications: z.array(z.string()).optional(),
    carbonFootprint: z.number().optional(),
    recyclabilityScore: z.number().min(0).max(100).optional(),
    expectedLifespan: z.number().optional(),
    repairabilityScore: z.number().min(0).max(10).optional(),
    description: z.string().optional(),
    templateImageUrls: z.array(z.string()).optional(),
    materialComposition: z.lazy(() => MaterialCompositionSchema).optional(),
    substancesOfConcern: z.array(z.lazy(() => SubstanceOfConcernSchema)).optional(),
    carbonFootprintData: z.lazy(() => CarbonFootprintDataSchema).optional(),
    certificationsStructured: z.array(z.lazy(() => CertificationSchema)).optional(),
    durabilityTests: z.array(z.lazy(() => DurabilityTestSchema)).optional(),
    endOfLifeData: z.lazy(() => EndOfLifeDataSchema).optional(),
    sparePartsCatalog: z.lazy(() => SparePartsCatalogSchema).optional(),
    hasBattery: z.boolean().optional(),
    batteryCapacityKwh: z.number().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().optional(),
});
export const CreateMasterProductSchema = z.object({
    name: z.string().min(1),
    sku: z.string().optional(),
    isActive: z.boolean().default(true),
    category: z.enum(ProductCategories),
    subcategory: z.string().optional(),
    model: z.string().optional(),
    gtin: z.string().optional(),
    manufacturer: z.string().optional(),
    manufacturerCountry: z.string().optional(),
    materials: z.array(z.unknown()).optional(),
    certifications: z.array(z.string()).optional(),
    carbonFootprint: z.number().optional(),
    recyclabilityScore: z.number().min(0).max(100).optional(),
    expectedLifespan: z.number().optional(),
    repairabilityScore: z.number().min(0).max(10).optional(),
    description: z.string().optional(),
    templateImageUrls: z.array(z.string()).optional(),
    materialComposition: z.lazy(() => MaterialCompositionSchema).optional(),
    substancesOfConcern: z.array(z.lazy(() => SubstanceOfConcernSchema)).optional(),
    carbonFootprintData: z.lazy(() => CarbonFootprintDataSchema).optional(),
    certificationsStructured: z.array(z.lazy(() => CertificationSchema)).optional(),
    durabilityTests: z.array(z.lazy(() => DurabilityTestSchema)).optional(),
    endOfLifeData: z.lazy(() => EndOfLifeDataSchema).optional(),
    sparePartsCatalog: z.lazy(() => SparePartsCatalogSchema).optional(),
    hasBattery: z.boolean().optional(),
    batteryCapacityKwh: z.number().optional(),
});
export const UpdateMasterProductSchema = CreateMasterProductSchema.partial().refine((v) => Object.keys(v).length > 0, { message: "At least one field must be provided" });
export const WorkOrderSchema = z.object({
    id: z.string(),
    organisationId: z.string(),
    assetId: z.string(),
    type: z.enum(WorkOrderTypes),
    priority: z.enum(Priorities),
    status: z.enum(WorkOrderStatuses),
    title: z.string().min(1),
    description: z.string().optional(),
    assignedTo: z.string().optional(),
    scheduledDate: z.string().optional(),
    completedDate: z.string().optional(),
    notes: z.string().optional(),
    createdBy: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().optional(),
});
export const CreateWorkOrderSchema = z.object({
    assetId: z.string(),
    type: z.enum(WorkOrderTypes),
    priority: z.enum(Priorities).default("MEDIUM"),
    title: z.string().min(1),
    description: z.string().optional(),
    assignedTo: z.string().optional(),
    scheduledDate: z.string().optional(),
});
export const UpdateWorkOrderSchema = z.object({
    priority: z.enum(Priorities).optional(),
    status: z.enum(WorkOrderStatuses).optional(),
    title: z.string().min(1).optional(),
    description: z.string().optional(),
    assignedTo: z.string().optional(),
    scheduledDate: z.string().optional(),
    notes: z.string().optional(),
    deletedAt: z.string().nullable().optional(), // null to restore, string to delete
}).refine((v) => Object.keys(v).length > 0, { message: "At least one field must be provided" });
export const IoTDeviceSchema = z.object({
    id: z.string(),
    organisationId: z.string(),
    assetId: z.string(),
    deviceType: z.enum(IoTDeviceTypes),
    manufacturer: z.string().optional(),
    model: z.string().optional(),
    firmwareVersion: z.string().optional(),
    status: z.enum(IoTDeviceStatuses),
    lastSeenAt: z.string().optional(),
    batteryLevel: z.number().min(0).max(100).optional(),
    signalStrength: z.number().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().optional(),
});
export const SensorReadingSchema = z.object({
    id: z.string(),
    deviceId: z.string(),
    assetId: z.string(),
    organisationId: z.string(),
    timestamp: z.string(),
    sensorType: z.enum(IoTDeviceTypes),
    value: z.number(),
    unit: z.string(),
    ttl: z.number().optional(),
});
// ============================================================================
// DPP COMPLIANCE ZOD SCHEMAS
// ============================================================================
// GS1 Identification Schema
export const Gs1IdentificationSchema = z.object({
    gtin: z.string().regex(/^\d{8,14}$/, "GTIN must be 8-14 digits"),
    batchNumber: z.string().optional(),
    serialNumber: z.string().optional(),
    productionDate: z.string().optional(),
    expirationDate: z.string().optional(),
});
// GS1 Location Schema
export const Gs1LocationSchema = z.object({
    gln: z.string().regex(/^\d{13}$/, "GLN must be 13 digits"),
    name: z.string().min(1),
    address: z.string().min(1),
    city: z.string().min(1),
    postalCode: z.string().optional(),
    country: z.string().min(1),
    countryCode: z.string().length(2),
});
// Economic Operator Schema
export const EconomicOperatorSchema = z.object({
    id: z.string(),
    role: z.enum(EconomicOperatorRoles),
    name: z.string().min(1),
    tradingName: z.string().optional(),
    gln: z.string().regex(/^\d{13}$/).optional(),
    address: z.string().min(1),
    city: z.string().min(1),
    postalCode: z.string().optional(),
    country: z.string().min(1),
    countryCode: z.string().length(2),
    registrationNumber: z.string().optional(),
    vatNumber: z.string().optional(),
    dunsNumber: z.string().optional(),
    contactEmail: z.string().email().optional(),
    contactPhone: z.string().optional(),
    website: z.string().url().optional(),
});
// Material Component Schema
export const MaterialComponentSchema = z.object({
    id: z.string(),
    name: z.string().min(1),
    materialType: z.string().min(1),
    weightGrams: z.number().positive(),
    percentageByWeight: z.number().min(0).max(100),
    isRecycled: z.boolean(),
    recycledContentPercent: z.number().min(0).max(100).optional(),
    isPreConsumerRecycled: z.boolean().optional(),
    isPostConsumerRecycled: z.boolean().optional(),
    isBioBased: z.boolean().optional(),
    bioBasedContentPercent: z.number().min(0).max(100).optional(),
    isRenewable: z.boolean().optional(),
    originCountry: z.string().optional(),
    supplierReference: z.string().optional(),
});
// Material Composition Schema
export const MaterialCompositionSchema = z.object({
    totalWeightGrams: z.number().positive(),
    components: z.array(MaterialComponentSchema),
    totalRecycledContentPercent: z.number().min(0).max(100),
    preConsumerRecycledPercent: z.number().min(0).max(100),
    postConsumerRecycledPercent: z.number().min(0).max(100),
    bioBasedContentPercent: z.number().min(0).max(100),
    renewableContentPercent: z.number().min(0).max(100),
    verificationBody: z.string().optional(),
    verificationDate: z.string().optional(),
    verificationDocumentUrl: z.string().url().optional(),
});
// Substance of Concern Schema
export const SubstanceOfConcernSchema = z.object({
    id: z.string(),
    category: z.enum(SubstanceCategories),
    chemicalName: z.string().min(1),
    commonName: z.string().optional(),
    casNumber: z.string().regex(/^\d{2,7}-\d{2}-\d$/, "Invalid CAS number format").optional(),
    ecNumber: z.string().optional(),
    locationInProduct: z.string().min(1),
    concentrationPercent: z.number().min(0).max(100),
    concentrationValue: z.number().optional(),
    concentrationUnit: z.string().optional(),
    safeHandlingInstructions: z.string().optional(),
    safeUseInstructions: z.string().optional(),
    disposalInstructions: z.string().optional(),
    reachAuthorizationNumber: z.string().optional(),
    exemptionReference: z.string().optional(),
});
// Carbon Footprint Phase Schema
export const CarbonFootprintPhaseSchema = z.object({
    phase: z.enum(CarbonLifecyclePhases),
    kgCO2e: z.number().min(0),
    percentageOfTotal: z.number().min(0).max(100),
    methodology: z.string().optional(),
    dataSource: z.string().optional(),
    dataQuality: z.enum(["PRIMARY", "SECONDARY", "ESTIMATED"]).optional(),
});
// Carbon Footprint Data Schema
export const CarbonFootprintDataSchema = z.object({
    totalKgCO2e: z.number().min(0),
    functionalUnit: z.string().min(1),
    phases: z.array(CarbonFootprintPhaseSchema),
    calculationMethodology: z.string().min(1),
    systemBoundary: z.string().optional(),
    verificationBody: z.string().optional(),
    verificationDate: z.string().optional(),
    epdReference: z.string().optional(),
    dataQualityRating: z.number().min(1).max(5).optional(),
    reportUrl: z.string().url().optional(),
});
// Certification Schema
export const CertificationSchema = z.object({
    id: z.string(),
    type: z.enum(CertificationTypes),
    name: z.string().min(1),
    certificateNumber: z.string().optional(),
    issuingBody: z.string().min(1),
    issueDate: z.string(),
    expiryDate: z.string().optional(),
    scope: z.string().optional(),
    documentUrl: z.string().url().optional(),
    verificationUrl: z.string().url().optional(),
});
// Durability Test Schema
export const DurabilityTestSchema = z.object({
    id: z.string(),
    testName: z.string().min(1),
    testStandard: z.string().min(1),
    testDate: z.string(),
    testingBody: z.string().min(1),
    result: z.enum(["PASS", "FAIL", "CONDITIONAL"]),
    cyclesCompleted: z.number().int().positive().optional(),
    loadAppliedKg: z.number().positive().optional(),
    testConditions: z.string().optional(),
    testReportReference: z.string().optional(),
    notes: z.string().optional(),
});
// Spare Part Schema
export const SparePartSchema = z.object({
    id: z.string(),
    partNumber: z.string().min(1),
    name: z.string().min(1),
    description: z.string().optional(),
    gtin: z.string().optional(),
    availability: z.enum(SparePartAvailabilities),
    availabilityEndDate: z.string().optional(),
    priceEur: z.number().min(0).optional(),
    priceGbp: z.number().min(0).optional(),
    currency: z.string().optional(),
    deliveryTimeDays: z.number().int().min(0).optional(),
    orderingUrl: z.string().url().optional(),
    orderingPhone: z.string().optional(),
    orderingEmail: z.string().email().optional(),
    compatibleModels: z.array(z.string()).optional(),
    installationDifficulty: z.enum(["EASY", "MODERATE", "PROFESSIONAL_REQUIRED"]).optional(),
    specialToolsRequired: z.boolean().optional(),
    toolsDescription: z.string().optional(),
    installationInstructionsUrl: z.string().url().optional(),
    imageUrl: z.string().url().optional(),
});
// Spare Parts Catalog Schema
export const SparePartsCatalogSchema = z.object({
    parts: z.array(SparePartSchema),
    catalogVersion: z.string(),
    lastUpdated: z.string(),
    minimumAvailabilityYears: z.number().int().min(1),
    manufacturerContactEmail: z.string().email().optional(),
    manufacturerContactPhone: z.string().optional(),
    warrantyTermsUrl: z.string().url().optional(),
    repairNetworkUrl: z.string().url().optional(),
});
// Disassembly Step Schema
export const DisassemblyStepSchema = z.object({
    stepNumber: z.number().int().positive(),
    instruction: z.string().min(1),
    toolsRequired: z.array(z.string()).optional(),
    estimatedTimeMinutes: z.number().int().positive().optional(),
    warningNotes: z.string().optional(),
    componentRemoved: z.string().optional(),
    materialType: z.string().optional(),
    recyclingStream: z.string().optional(),
    imageUrl: z.string().url().optional(),
    videoUrl: z.string().url().optional(),
});
// End of Life Data Schema
export const EndOfLifeDataSchema = z.object({
    disassemblyInstructions: z.array(DisassemblyStepSchema),
    totalDisassemblyTimeMinutes: z.number().int().positive().optional(),
    nonDestructiveRemovalPossible: z.boolean(),
    specialToolsRequired: z.array(z.string()).optional(),
    professionalDisassemblyRequired: z.boolean().optional(),
    recyclableMaterialsPercent: z.number().min(0).max(100),
    recoverableEnergyPercent: z.number().min(0).max(100),
    landfillPercent: z.number().min(0).max(100),
    hazardousWastePercent: z.number().min(0).max(100),
    recyclingFacilityRequirements: z.string().optional(),
    specialDisposalInstructions: z.string().optional(),
    hazardousComponentsWarning: z.string().optional(),
    takeBackSchemeAvailable: z.boolean(),
    takeBackSchemeUrl: z.string().url().optional(),
    takeBackSchemePhone: z.string().optional(),
    collectionPointsUrl: z.string().url().optional(),
    localRecyclingInfoUrl: z.string().url().optional(),
});
// Supply Chain Node Schema
export const SupplyChainNodeSchema = z.object({
    id: z.string(),
    tier: z.number().int().min(1),
    operator: EconomicOperatorSchema,
    componentSupplied: z.string().min(1),
    materialProvided: z.array(z.string()).optional(),
    percentageOfProduct: z.number().min(0).max(100).optional(),
    certifications: z.array(CertificationSchema).optional(),
    dueDiligenceStatus: z.enum(DueDiligenceStatuses),
    dueDiligenceDate: z.string().optional(),
    auditDate: z.string().optional(),
    auditResult: z.enum(["COMPLIANT", "NON_COMPLIANT", "PENDING", "CONDITIONAL"]).optional(),
    chainOfCustodyDocUrl: z.string().url().optional(),
    socialAuditCompleted: z.boolean().optional(),
    environmentalAuditCompleted: z.boolean().optional(),
    notes: z.string().optional(),
});
// Supply Chain Data Schema
export const SupplyChainDataSchema = z.object({
    nodes: z.array(SupplyChainNodeSchema),
    maxTierVisibility: z.number().int().min(1),
    dueDiligencePolicyUrl: z.string().url().optional(),
    dueDiligenceReportUrl: z.string().url().optional(),
    conflictMineralsFree: z.boolean().optional(),
    deforestationFree: z.boolean().optional(),
    forcedLaborFree: z.boolean().optional(),
    lastFullAuditDate: z.string().optional(),
    nextAuditDate: z.string().optional(),
    thirdPartyVerifierName: z.string().optional(),
    chainOfCustodyCertified: z.boolean().optional(),
});
// DPP Document Schema
export const DppDocumentSchema = z.object({
    id: z.string(),
    type: z.enum(DppDocumentTypes),
    title: z.string().min(1),
    description: z.string().optional(),
    documentUrl: z.string().url(),
    accessTier: z.enum(DppAccessTiers),
    language: z.string().length(2),
    version: z.string().optional(),
    uploadedAt: z.string(),
    validUntil: z.string().optional(),
    fileSize: z.number().int().positive().optional(),
    mimeType: z.string().optional(),
    checksumSha256: z.string().optional(),
});
// Battery Performance Schema
export const BatteryPerformanceSchema = z.object({
    ratedCapacityAh: z.number().positive(),
    ratedCapacityWh: z.number().positive(),
    nominalVoltage: z.number().positive(),
    maxVoltage: z.number().positive(),
    minVoltage: z.number().positive(),
    internalResistanceOhms: z.number().positive().optional(),
    roundTripEfficiencyPercent: z.number().min(0).max(100).optional(),
    expectedCycleLife: z.number().int().positive(),
    temperatureRangeMinC: z.number(),
    temperatureRangeMaxC: z.number(),
    stateOfHealth: z.number().min(0).max(100).optional(),
    stateOfCharge: z.number().min(0).max(100).optional(),
    remainingCapacityPercent: z.number().min(0).max(100).optional(),
    remainingEnergyWh: z.number().positive().optional(),
    cycleCount: z.number().int().min(0).optional(),
    lastMeasurementDate: z.string().optional(),
});
// Battery Carbon Footprint Schema
export const BatteryCarbonFootprintSchema = z.object({
    totalKgCO2e: z.number().min(0),
    kgCO2ePerKwh: z.number().min(0),
    productionKgCO2e: z.number().min(0),
    transportKgCO2e: z.number().min(0).optional(),
    calculationMethodology: z.string().min(1),
    carbonIntensityClass: z.enum(CarbonIntensityClasses),
    verificationBody: z.string().optional(),
    verificationDate: z.string().optional(),
    declarationUrl: z.string().url().optional(),
});
// Battery Material Composition Schema
export const BatteryMaterialCompositionSchema = z.object({
    cathodeActiveMaterial: z.string().min(1),
    anodeMaterial: z.string().min(1),
    electrolyte: z.string().min(1),
    separatorMaterial: z.string().optional(),
    casingMaterial: z.string().optional(),
    cobaltContentKg: z.number().min(0).optional(),
    lithiumContentKg: z.number().min(0).optional(),
    nickelContentKg: z.number().min(0).optional(),
    manganeseContentKg: z.number().min(0).optional(),
    graphiteContentKg: z.number().min(0).optional(),
    leadContentKg: z.number().min(0).optional(),
    recycledCobaltPercent: z.number().min(0).max(100).optional(),
    recycledLithiumPercent: z.number().min(0).max(100).optional(),
    recycledNickelPercent: z.number().min(0).max(100).optional(),
    recycledLeadPercent: z.number().min(0).max(100).optional(),
    hazardousSubstances: z.array(SubstanceOfConcernSchema).optional(),
});
// Battery Passport Schema
export const BatteryPassportSchema = z.object({
    id: z.string(),
    batteryPassportId: z.string(),
    linkedProductId: z.string().optional(),
    category: z.enum(BatteryCategories),
    chemistry: z.enum(BatteryChemistries),
    chemistryDetails: z.string().optional(),
    manufacturer: EconomicOperatorSchema,
    manufacturingDate: z.string(),
    manufacturingLocation: Gs1LocationSchema.optional(),
    batteryModel: z.string().min(1),
    batteryWeightKg: z.number().positive(),
    performance: BatteryPerformanceSchema,
    carbonFootprint: BatteryCarbonFootprintSchema,
    materialComposition: BatteryMaterialCompositionSchema,
    supplyChain: SupplyChainDataSchema.optional(),
    collectionSchemeUrl: z.string().url().optional(),
    recyclingInstructions: z.string().optional(),
    secondLifeApplications: z.array(z.string()).optional(),
    safeRemovalInstructions: z.string().optional(),
    euDeclarationOfConformityUrl: z.string().url(),
    ukDeclarationOfConformityUrl: z.string().url().optional(),
    testReportsAvailable: z.boolean(),
    certifications: z.array(CertificationSchema).optional(),
    qrCodeUrl: z.string().url().optional(),
    digitalLinkUri: z.string().optional(),
    dppVersion: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// DPP Fields Schema
export const DppPassportFieldsSchema = z.object({
    // Identification
    dppId: z.string().optional(),
    productIdentifierType: z.enum(ProductIdentifierTypes).optional(),
    productIdentifierValue: z.string().optional(),
    dppProductType: z.enum(DppProductTypes).optional(),
    variantModel: z.string().optional(),
    brandTradeName: z.string().optional(),
    productCategoryEU: z.enum(ProductCategoriesEU).optional(),
    // Economic Operator
    responsibleOperatorRole: z.enum(EconomicOperatorRoles).optional(),
    responsibleOperatorIdType: z.enum(EconomicOperatorIdTypes).optional(),
    responsibleOperatorId: z.string().optional(),
    manufacturerName: z.string().optional(),
    manufacturerCountryCode: z.string().length(2).optional(),
    // Production
    productionSiteId: z.string().optional(),
    productionCountryCode: z.string().length(2).optional(),
    batchLotSerial: z.string().optional(),
    // Data Carrier
    dataCarrierType: z.enum(DataCarrierTypes).optional(),
    dataCarrierLocation: z.enum(DataCarrierLocations).optional(),
    // Data Management
    dataAccessLevel: z.enum(DataAccessLevels).optional(),
    dataSourceType: z.enum(DataSourceTypes).optional(),
    dataUpdatePolicy: z.enum(DataUpdatePolicies).optional(),
    dataRetentionPeriod: z.enum(DataRetentionPeriods).optional(),
    personalDataIncluded: z.enum(PersonalDataOptions).optional(),
    // Product Use Context
    productUseContext: z.enum(ProductUseContexts).optional(),
    environment: z.enum(EnvironmentTypes).optional(),
    userGroup: z.enum(UserGroupTypes).optional(),
    useSeverityClass: z.enum(UseSeverityClasses).optional(),
    loadRating: z.enum(LoadRatings).optional(),
    impactVandalClass: z.enum(ImpactVandalClasses).optional(),
    firePerformanceClass: z.enum(FirePerformanceClasses).optional(),
    // Safety & Standards
    safetyStandardApplied: z.enum(SafetyStandards).optional(),
    intendedUse: z.enum(IntendedUseTypes).optional(),
    // Accessibility
    accessibilityDesign: z.enum(AccessibilityDesigns).optional(),
    wheelchairInterface: z.enum(WheelchairInterfaces).optional(),
    armrestsSupports: z.enum(ArmrestOptions).optional(),
    heightCategory: z.enum(HeightCategories).optional(),
    tactileVisualCues: z.enum(TactileVisualCues).optional(),
    // Materials
    primaryMaterialGroup: z.enum(PrimaryMaterialGroups).optional(),
    certifiedWoodSource: z.enum(CertifiedWoodSources).optional(),
    recycledContentBand: z.enum(RecycledContentBands).optional(),
    hazardousSubstancesPresent: z.enum(HazardousSubstanceFlags).optional(),
    corrosionProtectionClass: z.enum(CorrosionProtectionClasses).optional(),
    uvWeatherResistance: z.enum(UvWeatherResistances).optional(),
    surfaceTreatment: z.enum(SurfaceTreatments).optional(),
    // Environmental
    epdLcaReference: z.enum(EpdLcaReferences).optional(),
    epdLcaReferenceId: z.string().optional(),
    carbonFootprintScope: z.enum(CarbonFootprintScopes).optional(),
    carbonFootprintValueBand: z.enum(CarbonFootprintValueBands).optional(),
    // Installation
    mountingMethod: z.enum(MountingMethods).optional(),
    typicalInstallationSetting: z.enum(InstallationSettings).optional(),
    // Maintenance
    inspectionRegime: z.enum(InspectionRegimes).optional(),
    cleaningFrequency: z.enum(CleaningFrequencies).optional(),
    approvedCleaningAgents: z.enum(ApprovedCleaningAgents).optional(),
    serviceableComponents: z.array(z.enum(ServiceableComponentTypes)).optional(),
    sparePartsKitId: z.enum(SparePartsKitIds).optional(),
    // Lifecycle
    expectedServiceLife: z.enum(ExpectedServiceLifeBands).optional(),
    repairabilityRatingDpp: z.enum(RepairabilityRatings).optional(),
    endOfLifeRoute: z.enum(EndOfLifeRoutes).optional(),
    takeBackScheme: z.enum(TakeBackSchemes).optional(),
    // Compliance
    conformityMarking: z.enum(ConformityMarkings).optional(),
    euLegislationReferences: z.array(z.enum(EuLegislationReferences)).optional(),
    gppCriteriaMet: z.enum(GppCriteriaOptions).optional(),
});
// Human-readable labels for product categories
export const ProductCategoryLabels = {
    seating: "Seating",
    litter_recycling: "Litter & Recycling",
    shelters: "Shelters",
    cycle: "Cycle Infrastructure",
    bollards_barriers: "Bollards & Barriers",
    signage_display: "Signage & Display",
    solar_power: "Solar & Power",
    planters: "Planters",
    lighting: "Lighting",
    other: "Other",
};
// ============================================================================
// DPP FIELD LABELS (Human-readable labels for all DPP dropdown values)
// ============================================================================
export const ProductIdentifierTypeLabels = {
    GTIN: "GTIN",
    PROPRIETARY_PRODUCT_CODE: "Proprietary product code",
    MODEL_NUMBER: "Model number",
    TYPE_CODE: "Type code",
    OTHER: "Other",
};
export const DppProductTypeLabels = {
    BENCH: "Bench",
    LITTER_BIN: "Litter bin",
    RECYCLING_BIN: "Recycling bin",
    PICNIC_TABLE: "Picnic table",
    BUS_SHELTER: "Bus shelter",
    QUEUE_BARRIER: "Queue barrier",
    PLANTER: "Planter",
    BOLLARD: "Bollard",
    SIGNAGE: "Signage",
    OTHER: "Other",
};
export const ProductCategoryEULabels = {
    FURNITURE: "Furniture",
    CONSTRUCTION_PRODUCT: "Construction product",
    ELECTRICAL_ELECTRONIC: "Electrical/electronic",
    OTHER_PHYSICAL_GOOD: "Other physical good",
};
export const EconomicOperatorRoleLabels = {
    MANUFACTURER: "Manufacturer",
    AUTHORIZED_REPRESENTATIVE: "Authorised representative",
    IMPORTER: "Importer",
    DISTRIBUTOR: "Distributor",
    FULFILLMENT_SERVICE_PROVIDER: "Fulfilment service provider",
};
export const EconomicOperatorIdTypeLabels = {
    VAT_NUMBER: "VAT number",
    NATIONAL_ID: "National ID",
    EORI: "EORI",
    OTHER: "Other",
};
export const DataCarrierTypeLabels = {
    QR_CODE: "QR code",
    DATA_MATRIX_2D: "2D barcode (Data Matrix)",
    BARCODE_1D: "1D barcode",
    RFID_NFC: "RFID/NFC",
    PRINTED_URL: "Printed URL",
    OTHER: "Other",
};
export const DataCarrierLocationLabels = {
    ON_PRODUCT: "On product",
    ON_PACKAGING: "On packaging",
    ON_DOCUMENTATION: "On documentation",
    MULTIPLE_LOCATIONS: "Multiple locations",
};
export const DataAccessLevelLabels = {
    PUBLIC_ONLY: "Public data only",
    PUBLIC_PLUS_PROFESSIONAL: "Public + restricted professional layer",
    PUBLIC_PLUS_B2B: "Public + restricted B2B layer",
};
export const DataSourceTypeLabels = {
    MANUFACTURER_SYSTEM: "Manufacturer system",
    IMPORTER_SYSTEM: "Importer system",
    THIRD_PARTY_SERVICE: "Third party service",
    MIXED_SOURCES: "Mixed sources",
};
export const DataUpdatePolicyLabels = {
    STATIC: "Static (at placement)",
    PERIODIC_UPDATES: "Periodic updates",
    EVENT_BASED_UPDATES: "Event-based updates",
    REAL_TIME_SYNC: "Real-time/sync",
};
export const DataRetentionPeriodLabels = {
    PRODUCT_LIFETIME: "Product lifetime",
    FIXED_YEARS: "Fixed years (e.g. 10 years)",
    UNTIL_EOL_DECLARED: "Until EoL declared",
    OTHER: "Other",
};
export const PersonalDataOptionLabels = {
    NONE: "None",
    MINIMAL_WITH_CONSENT: "Minimal (with consent)",
    NOT_APPLICABLE: "Not applicable",
};
export const ProductUseContextLabels = {
    PUBLIC_STREET_PARK: "Public street/park",
    SCHOOL_UNIVERSITY: "School/university",
    TRANSPORT_HUB: "Transport hub",
    THEME_PARK: "Theme park",
    INDOOR_ATTRACTION: "Indoor attraction",
    RETAIL_LEISURE: "Retail/leisure",
    MIXED_USE: "Mixed use",
};
export const EnvironmentTypeLabels = {
    OUTDOOR_UNCOVERED: "Outdoor uncovered",
    OUTDOOR_PARTIALLY_COVERED: "Outdoor partially covered",
    INDOOR_PUBLIC: "Indoor public",
    INDOOR_CONTROLLED: "Indoor controlled",
};
export const UserGroupTypeLabels = {
    GENERAL_PUBLIC: "General public",
    CHILDREN: "Children",
    ADULTS: "Adults",
    MIXED_AGES: "Mixed ages",
    REDUCED_MOBILITY_USERS: "Reduced mobility users",
    STAFF_ONLY: "Staff only",
};
export const UseSeverityClassLabels = {
    LIGHT_DUTY: "Light duty",
    STANDARD_DUTY: "Standard duty",
    HEAVY_DUTY: "Heavy duty",
    EXTREME_VANDAL_PRONE: "Extreme/vandal prone",
};
export const LoadRatingLabels = {
    STANDARD_PUBLIC_SEATING: "Standard public seating",
    HIGH_LOAD_SEATING: "High/load seating",
    STANDING_LEANING_SUPPORT: "Standing/leaning support",
    STRUCTURAL_SHELTER: "Structural shelter",
    NOT_LOAD_BEARING: "Not load bearing",
};
export const ImpactVandalClassLabels = {
    NONE_DECLARED: "None declared",
    BASIC: "Basic",
    ANTI_VANDAL: "Anti-vandal",
    HIGH_ANTI_VANDAL: "High anti-vandal",
    SECURITY_GRADE: "Security grade",
};
export const FirePerformanceClassLabels = {
    NOT_APPLICABLE: "Not applicable",
    CLASS_A: "Class A",
    CLASS_B: "Class B",
    CLASS_C: "Class C",
    CLASS_D: "Class D",
    CLASS_E: "Class E",
    OTHER_SEE_DOCS: "Other/see docs",
};
export const SafetyStandardLabels = {
    NONE: "None",
    EN_1176_PLAY: "EN 1176 (play)",
    EN_13200_SPECTATOR: "EN 13200 (spectator/stands)",
    LOCAL_THEME_PARK_STANDARD: "Local theme park standard",
    OTHER: "Other",
};
export const IntendedUseTypeLabels = {
    SEATING: "Seating",
    WASTE_COLLECTION: "Waste collection",
    SHELTER: "Shelter",
    QUEUE_MANAGEMENT: "Queue management",
    WAYFINDING: "Wayfinding",
    MIXED: "Mixed",
    OTHER: "Other",
};
export const AccessibilityDesignLabels = {
    NOT_SPECIFICALLY_ACCESSIBLE: "Not specifically accessible",
    PARTIALLY_ACCESSIBLE: "Partially accessible",
    FULLY_ACCESSIBLE: "Fully accessible (design intent)",
};
export const WheelchairInterfaceLabels = {
    NOT_SUITABLE: "Not suitable",
    PARTIALLY_SUITABLE: "Partially suitable",
    FULLY_SUITABLE: "Fully suitable",
    NOT_APPLICABLE: "Not applicable",
};
export const ArmrestOptionLabels = {
    NONE: "None",
    ARMRESTS_BOTH_SIDES: "Armrests both sides",
    ARMRESTS_SOME_SEATS: "Armrests some seats",
    BACKREST_AND_ARMRESTS: "Backrest & armrests",
    NOT_APPLICABLE: "Not applicable",
};
export const HeightCategoryLabels = {
    LOW: "Low",
    STANDARD: "Standard",
    HIGH: "High",
    ADJUSTABLE: "Adjustable",
    NOT_APPLICABLE: "Not applicable",
};
export const TactileVisualCueLabels = {
    NONE: "None",
    HIGH_CONTRAST_EDGES: "High contrast edges",
    TACTILE_SURFACES: "Tactile surfaces",
    BOTH_TACTILE_AND_HIGH_CONTRAST: "Both tactile and high contrast",
};
export const PrimaryMaterialGroupLabels = {
    WOOD: "Wood",
    METAL_STEEL: "Metal (steel)",
    METAL_ALUMINIUM: "Metal (aluminium)",
    PLASTIC: "Plastic",
    COMPOSITE: "Composite",
    CONCRETE_STONE: "Concrete/stone",
    MIXED: "Mixed",
};
export const CertifiedWoodSourceLabels = {
    NOT_APPLICABLE: "Not applicable",
    FSC_CERTIFIED: "FSC certified",
    PEFC_CERTIFIED: "PEFC certified",
    OTHER_CERTIFIED: "Other certified",
    UNCERTIFIED: "Uncertified",
};
export const RecycledContentBandLabels = {
    "0_TO_9_PERCENT": "0-9%",
    "10_TO_29_PERCENT": "10-29%",
    "30_TO_49_PERCENT": "30-49%",
    "50_TO_79_PERCENT": "50-79%",
    "80_PLUS_PERCENT": "80%+",
    NOT_DECLARED: "Not declared",
};
export const HazardousSubstanceFlagLabels = {
    NONE: "None",
    CONTAINS_RESTRICTED_SEE_DOCS: "Contains restricted substances (see docs)",
    UNKNOWN: "Unknown",
};
export const CorrosionProtectionClassLabels = {
    NOT_APPLICABLE: "Not applicable",
    LOW: "Low",
    MEDIUM: "Medium",
    HIGH_COASTAL_INDUSTRIAL: "High (coastal/industrial)",
};
export const UvWeatherResistanceLabels = {
    NOT_APPLICABLE: "Not applicable",
    INDOOR_ONLY: "Indoor only",
    OUTDOOR_STANDARD: "Outdoor standard",
    OUTDOOR_HIGH_UV: "Outdoor high-UV",
    MARINE_COASTAL: "Marine/coastal",
};
export const SurfaceTreatmentLabels = {
    NONE: "None",
    PAINT: "Paint",
    POWDER_COAT: "Powder coat",
    GALVANISED: "Galvanised",
    ANODISED: "Anodised",
    WOOD_PRESERVATIVE: "Wood preservative",
    ANTI_GRAFFITI_COATING: "Anti-graffiti coating",
    OTHER: "Other",
};
export const EpdLcaReferenceLabels = {
    NONE: "None",
    EPD_AVAILABLE: "EPD available (ID/link)",
    OTHER_LCA_AVAILABLE: "Other LCA available",
};
export const CarbonFootprintScopeLabels = {
    PRODUCT_STAGE_ONLY: "Product stage only",
    CRADLE_TO_GATE: "Cradle-to-gate",
    CRADLE_TO_GRAVE: "Cradle-to-grave",
    CRADLE_TO_GRAVE_INCL_B2B_USE: "Cradle-to-grave incl. B2B use",
    NOT_DECLARED: "Not declared",
};
export const CarbonFootprintValueBandLabels = {
    VERY_LOW: "Very low",
    LOW: "Low",
    MEDIUM: "Medium",
    HIGH: "High",
    VERY_HIGH: "Very high",
    NOT_DECLARED: "Not declared",
};
export const MountingMethodLabels = {
    FREESTANDING: "Freestanding",
    SURFACE_FIXED_BOLTED: "Surface-fixed (bolted)",
    SUB_SURFACE_ANCHORS_FOUNDATIONS: "Sub-surface (anchors/foundations)",
    WALL_MOUNTED: "Wall-mounted",
    INTEGRATED_STRUCTURE: "Integrated structure",
};
export const InstallationSettingLabels = {
    PAVEMENT: "Pavement",
    SOFT_LANDSCAPING: "Soft landscaping",
    PLATFORM: "Platform",
    INDOOR_FLOOR: "Indoor floor",
    WALL: "Wall",
    OTHER: "Other",
};
export const InspectionRegimeLabels = {
    WEEKLY: "Weekly",
    MONTHLY: "Monthly",
    QUARTERLY: "Quarterly",
    ANNUALLY: "Annually",
    PER_LOCAL_RISK_ASSESSMENT: "Per local risk assessment",
};
export const CleaningFrequencyLabels = {
    DAILY: "Daily",
    SEVERAL_TIMES_PER_WEEK: "Several times per week",
    WEEKLY: "Weekly",
    AS_NEEDED: "As needed",
};
export const ApprovedCleaningAgentLabels = {
    WATER_ONLY: "Water only",
    NEUTRAL_DETERGENT: "Neutral detergent",
    SOLVENT_BASED_ALLOWED: "Solvent-based allowed",
    SPECIAL_GRAFFITI_REMOVER_ONLY: "Special graffiti remover only",
    SEE_INSTRUCTIONS: "See instructions",
};
export const ServiceableComponentTypeLabels = {
    NONE: "None",
    SEATING_SLATS: "Seating slats",
    PANELS_GLAZING: "Panels/glazing",
    FIXINGS_ANCHORS: "Fixings/anchors",
    BIN_LINER_INNER: "Bin liner/inner",
    SIGN_PANEL: "Sign panel",
    OTHER: "Other",
};
export const SparePartsKitIdLabels = {
    NONE: "None",
    STANDARD_KIT: "Standard kit",
    ANTI_VANDAL_KIT: "Anti-vandal kit",
    THEME_PARK_CUSTOM_KIT: "Theme park custom kit",
};
export const ExpectedServiceLifeBandLabels = {
    LESS_THAN_5_YEARS: "<5 years",
    "5_TO_9_YEARS": "5-9 years",
    "10_TO_14_YEARS": "10-14 years",
    "15_TO_24_YEARS": "15-24 years",
    "25_PLUS_YEARS": ">25 years",
    NOT_DECLARED: "Not declared",
};
export const RepairabilityRatingLabels = {
    NOT_REPAIRABLE: "Not repairable",
    LIMITED_REPAIR: "Limited repair",
    REPAIRABLE_BASIC_TOOLS: "Repairable with basic tools",
    MODULAR_FULLY_REPAIRABLE: "Modular/fully repairable",
};
export const EndOfLifeRouteLabels = {
    LANDFILL_INCINERATION: "Landfill/incineration",
    ENERGY_RECOVERY: "Energy recovery",
    MATERIAL_RECYCLING: "Material recycling",
    COMPONENT_REUSE: "Component reuse",
    REFURBISH_REPURPOSE: "Refurbish/repurpose",
};
export const TakeBackSchemeLabels = {
    NONE: "None",
    VOLUNTARY_TAKE_BACK: "Voluntary take-back",
    CONTRACTED_TAKE_BACK_B2B: "Contracted take-back (B2B)",
    THIRD_PARTY_SCHEME: "Third party scheme",
};
export const ConformityMarkingLabels = {
    NOT_DECLARED: "Not declared",
    CE_UKCA_OTHER_APPLICABLE: "CE/UKCA/other marking applicable",
    IN_CONFORMITY_WITH_EU_LAW: "In conformity with applicable EU law",
};
export const EuLegislationReferenceLabels = {
    ESPR_2024_1781: "ESPR (2024/1781)",
    CONSTRUCTION_PRODUCTS_RULES: "Construction Products rules",
    GENERAL_PRODUCT_SAFETY: "General Product Safety",
    OTHER_SEE_DOCS: "Other/see docs",
};
export const GppCriteriaOptionLabels = {
    NONE_DECLARED: "None declared",
    MEETS_EU_GPP_CORE: "Meets EU GPP core",
    MEETS_EU_GPP_COMPREHENSIVE: "Meets EU GPP comprehensive",
    MEETS_BUYER_SPECIFIC_CRITERIA: "Meets buyer specific criteria",
};
// DPP Field Metadata (for UI form generation)
export const DppPassportFieldMeta = {
    dppId: { label: "DPP ID / URI", type: "text", readonly: true, description: "Unique passport identifier (system generated)" },
    productIdentifierType: { label: "Product identifier type", type: "select", options: ProductIdentifierTypes },
    productIdentifierValue: { label: "Product identifier value", type: "text", placeholder: "Product ID code" },
    dppProductType: { label: "Product type", type: "select", options: DppProductTypes },
    variantModel: { label: "Variant / model", type: "text", placeholder: "Variant or configuration" },
    brandTradeName: { label: "Brand / trade name", type: "text", placeholder: "Brand shown to users" },
    productCategoryEU: { label: "Product category (EU)", type: "select", options: ProductCategoriesEU },
    responsibleOperatorRole: { label: "Responsible operator role", type: "select", options: EconomicOperatorRoles },
    responsibleOperatorIdType: { label: "Responsible operator ID type", type: "select", options: EconomicOperatorIdTypes },
    responsibleOperatorId: { label: "Responsible operator ID", type: "text", placeholder: "Operator identifier value" },
    manufacturerName: { label: "Manufacturer name", type: "text", placeholder: "Legal manufacturer" },
    manufacturerCountryCode: { label: "Manufacturer country", type: "select", description: "Country of manufacturer" },
    productionSiteId: { label: "Production site ID", type: "text", placeholder: "Site identifier" },
    productionCountryCode: { label: "Production country", type: "select", description: "Country of production" },
    batchLotSerial: { label: "Batch / lot / serial", type: "text", placeholder: "Batch or serial code" },
    dataCarrierType: { label: "Data carrier type", type: "select", options: DataCarrierTypes },
    dataCarrierLocation: { label: "Data carrier location", type: "select", options: DataCarrierLocations },
    dataAccessLevel: { label: "Data access level", type: "select", options: DataAccessLevels },
    dataSourceType: { label: "Data source type", type: "select", options: DataSourceTypes },
    dataUpdatePolicy: { label: "Data update policy", type: "select", options: DataUpdatePolicies },
    dataRetentionPeriod: { label: "Data retention period", type: "select", options: DataRetentionPeriods },
    personalDataIncluded: { label: "Personal data included", type: "select", options: PersonalDataOptions },
    productUseContext: { label: "Product use context", type: "select", options: ProductUseContexts },
    environment: { label: "Environment", type: "select", options: EnvironmentTypes },
    userGroup: { label: "User group", type: "select", options: UserGroupTypes },
    useSeverityClass: { label: "Use severity class", type: "select", options: UseSeverityClasses },
    loadRating: { label: "Load rating", type: "select", options: LoadRatings },
    impactVandalClass: { label: "Impact / vandal class", type: "select", options: ImpactVandalClasses },
    firePerformanceClass: { label: "Fire performance class", type: "select", options: FirePerformanceClasses },
    safetyStandardApplied: { label: "Safety standard applied", type: "select", options: SafetyStandards },
    intendedUse: { label: "Intended use", type: "select", options: IntendedUseTypes },
    accessibilityDesign: { label: "Accessibility design", type: "select", options: AccessibilityDesigns },
    wheelchairInterface: { label: "Wheelchair interface", type: "select", options: WheelchairInterfaces },
    armrestsSupports: { label: "Armrests / supports", type: "select", options: ArmrestOptions },
    heightCategory: { label: "Height category", type: "select", options: HeightCategories },
    tactileVisualCues: { label: "Tactile / visual cues", type: "select", options: TactileVisualCues },
    primaryMaterialGroup: { label: "Primary material group", type: "select", options: PrimaryMaterialGroups },
    certifiedWoodSource: { label: "Certified wood source", type: "select", options: CertifiedWoodSources },
    recycledContentBand: { label: "Recycled content band", type: "select", options: RecycledContentBands },
    hazardousSubstancesPresent: { label: "Hazardous substances present", type: "select", options: HazardousSubstanceFlags },
    corrosionProtectionClass: { label: "Corrosion protection class", type: "select", options: CorrosionProtectionClasses },
    uvWeatherResistance: { label: "UV / weather resistance", type: "select", options: UvWeatherResistances },
    surfaceTreatment: { label: "Surface treatment", type: "select", options: SurfaceTreatments },
    epdLcaReference: { label: "EPD / LCA reference", type: "select", options: EpdLcaReferences },
    epdLcaReferenceId: { label: "EPD / LCA reference ID", type: "text", placeholder: "Link or ID" },
    carbonFootprintScope: { label: "Carbon footprint scope", type: "select", options: CarbonFootprintScopes },
    carbonFootprintValueBand: { label: "Carbon footprint value band", type: "select", options: CarbonFootprintValueBands },
    mountingMethod: { label: "Mounting method", type: "select", options: MountingMethods },
    typicalInstallationSetting: { label: "Typical installation setting", type: "select", options: InstallationSettings },
    inspectionRegime: { label: "Inspection regime", type: "select", options: InspectionRegimes },
    cleaningFrequency: { label: "Cleaning frequency", type: "select", options: CleaningFrequencies },
    approvedCleaningAgents: { label: "Approved cleaning agents", type: "select", options: ApprovedCleaningAgents },
    serviceableComponents: { label: "Serviceable components", type: "multiselect", options: ServiceableComponentTypes },
    sparePartsKitId: { label: "Spare parts kit ID", type: "select", options: SparePartsKitIds },
    expectedServiceLife: { label: "Expected service life (outdoor)", type: "select", options: ExpectedServiceLifeBands },
    repairabilityRatingDpp: { label: "Repairability rating", type: "select", options: RepairabilityRatings },
    endOfLifeRoute: { label: "End of life route", type: "select", options: EndOfLifeRoutes },
    takeBackScheme: { label: "Take back / producer scheme", type: "select", options: TakeBackSchemes },
    conformityMarking: { label: "Legal / conformity marking", type: "select", options: ConformityMarkings },
    euLegislationReferences: { label: "Key EU legislation references", type: "multiselect", options: EuLegislationReferences },
    gppCriteriaMet: { label: "GPP / internal criteria met", type: "select", options: GppCriteriaOptions },
};
export const ProductFieldMeta = {
    id: { label: "Product ID", type: "text", readonly: true, hidden: true },
    passportId: { label: "Passport ID", type: "text", readonly: true, description: "Digital Product Passport identifier" },
    name: { label: "Name", type: "text", required: true, placeholder: "Enter product name" },
    category: { label: "Category", type: "select", options: ProductCategories, required: true },
    subcategory: { label: "Subcategory", type: "text", placeholder: "e.g., Bench, Bus Shelter, Rising Bollard" },
    model: { label: "Model", type: "text" },
    serialNumber: { label: "Serial Number", type: "text" },
    gtin: { label: "GTIN", type: "text", description: "Global Trade Item Number" },
    installationSite: { label: "Installation Site", type: "text" },
    installationDate: { label: "Installation Date", type: "date" },
    status: { label: "Status", type: "select", options: ProductStatuses, required: true },
    condition: { label: "Condition", type: "select", options: ConditionRatings },
    manufacturer: { label: "Manufacturer", type: "text" },
    manufacturerCountry: { label: "Country of Origin", type: "text" },
    description: { label: "Description", type: "textarea" },
    notes: { label: "Notes", type: "textarea" },
};
// Backwards compatibility alias
export const AssetFieldMeta = ProductFieldMeta;
export const WorkOrderFieldMeta = {
    id: { label: "Work Order ID", type: "text", readonly: true, hidden: true },
    title: { label: "Title", type: "text", required: true },
    type: { label: "Type", type: "select", options: WorkOrderTypes, required: true },
    priority: { label: "Priority", type: "select", options: Priorities, required: true },
    status: { label: "Status", type: "select", options: WorkOrderStatuses, required: true },
    description: { label: "Description", type: "textarea" },
    assignedTo: { label: "Assigned To", type: "text" },
    scheduledDate: { label: "Scheduled Date", type: "date" },
    notes: { label: "Notes", type: "textarea" },
};
// ============================================================================
// UTILITIES
// ============================================================================
export const nowIso = () => new Date().toISOString();
export const ttlInDays = (days) => Math.floor((Date.now() + days * 86400000) / 1000);
export const generateId = (prefix) => `${prefix}-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
export const generatePassportId = () => `DPP-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
export const generateMasterProductId = () => `MP-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
// ============================================================================
// JSON-LD CONTEXT (for EU DPP compliance - schema.org + GS1 Web Vocabulary)
// ============================================================================
/**
 * JSON-LD Context for EU Digital Product Passport
 * Combines schema.org (primary), GS1 Web Vocabulary, and ESF custom namespace
 */
export const DppJsonLdContext = {
    "@context": {
        // Primary vocabularies
        "@vocab": "https://schema.org/",
        "gs1": "https://gs1.org/voc/",
        "esf": "https://esf.digital/dpp/vocab/",
        "xsd": "http://www.w3.org/2001/XMLSchema#",
        // GS1 Product Identification
        "gtin": "gs1:gtin",
        "gtin14": "gs1:gtin14",
        "serialNumber": "gs1:serialNumber",
        "batchLotNumber": "gs1:batchLotNumber",
        "productionDate": "gs1:productionDate",
        "gln": "gs1:gln",
        // Schema.org mappings
        "manufacturer": "schema:manufacturer",
        "brand": "schema:brand",
        "model": "schema:model",
        "productID": "schema:productID",
        "material": "schema:material",
        "weight": "schema:weight",
        "width": "schema:width",
        "height": "schema:height",
        "depth": "schema:depth",
        // ESF/DPP specific properties
        "passportId": "esf:passportId",
        "dppVersion": "esf:dppVersion",
        "accessTier": "esf:accessTier",
        // Sustainability
        "carbonFootprint": "esf:carbonFootprint",
        "carbonFootprintValue": { "@id": "esf:carbonFootprintValue", "@type": "xsd:decimal" },
        "recyclabilityScore": { "@id": "esf:recyclabilityScore", "@type": "xsd:integer" },
        "repairabilityScore": { "@id": "esf:repairabilityScore", "@type": "xsd:integer" },
        "recycledContent": "esf:recycledContent",
        "preConsumerRecycledPercent": { "@id": "esf:preConsumerRecycledPercent", "@type": "xsd:decimal" },
        "postConsumerRecycledPercent": { "@id": "esf:postConsumerRecycledPercent", "@type": "xsd:decimal" },
        // Substances of concern
        "substanceOfConcern": "esf:substanceOfConcern",
        "casNumber": "esf:casNumber",
        "ecNumber": "esf:ecNumber",
        // End of life
        "endOfLife": "esf:endOfLife",
        "disassemblyInstructions": "esf:disassemblyInstructions",
        "recyclableMaterialsPercent": { "@id": "esf:recyclableMaterialsPercent", "@type": "xsd:decimal" },
        // Supply chain
        "supplyChain": "esf:supplyChain",
        "supplierTier": { "@id": "esf:supplierTier", "@type": "xsd:integer" },
        "dueDiligenceStatus": "esf:dueDiligenceStatus",
        // Battery passport (EU Battery Regulation)
        "batteryPassport": "esf:batteryPassport",
        "stateOfHealth": { "@id": "esf:stateOfHealth", "@type": "xsd:decimal" },
        "cycleCount": { "@id": "esf:cycleCount", "@type": "xsd:integer" },
        "batteryCapacity": "esf:batteryCapacity",
        "carbonIntensityClass": "esf:carbonIntensityClass",
        // Certifications
        "certification": "schema:certification",
        "certificateNumber": "esf:certificateNumber",
        "issuingBody": "esf:issuingBody",
        // Documents
        "document": "schema:DigitalDocument",
        "documentUrl": "schema:url",
    },
};
// ============================================================================
// GS1 DIGITAL LINK UTILITIES
// ============================================================================
/**
 * Generate a GS1 Digital Link URI for a product
 * Format: https://[domain]/01/[GTIN]/10/[batch]/21/[serial]
 */
export function generateDigitalLinkUri(domain, gtin, batchNumber, serialNumber) {
    // Ensure GTIN is 14 digits (pad with leading zeros if needed)
    const gtin14 = gtin.padStart(14, "0");
    let uri = `https://${domain}/01/${gtin14}`;
    if (batchNumber) {
        uri += `/10/${encodeURIComponent(batchNumber)}`;
    }
    if (serialNumber) {
        uri += `/21/${encodeURIComponent(serialNumber)}`;
    }
    return uri;
}
/**
 * Parse a GS1 Digital Link URI into its components
 */
export function parseDigitalLinkUri(uri) {
    const pattern = /^https?:\/\/([^\/]+)\/01\/(\d{8,14})(?:\/10\/([^\/]+))?(?:\/21\/([^\/]+))?/;
    const match = uri.match(pattern);
    if (!match)
        return null;
    return {
        domain: match[1],
        gtin: match[2],
        batchNumber: match[3] ? decodeURIComponent(match[3]) : undefined,
        serialNumber: match[4] ? decodeURIComponent(match[4]) : undefined,
    };
}
/**
 * Generate a unique Battery Passport ID
 */
export const generateBatteryPassportId = () => `BAT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
/**
 * Validate a CAS Registry Number
 * Format: XXXXX-XX-X (2-7 digits, 2 digits, 1 check digit)
 */
export function isValidCasNumber(cas) {
    const pattern = /^(\d{2,7})-(\d{2})-(\d)$/;
    const match = cas.match(pattern);
    if (!match)
        return false;
    // Validate check digit
    const digits = (match[1] + match[2]).split("").reverse();
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
        sum += parseInt(digits[i]) * (i + 1);
    }
    const checkDigit = sum % 10;
    return checkDigit === parseInt(match[3]);
}
/**
 * Validate a GTIN (Global Trade Item Number)
 * Supports GTIN-8, GTIN-12, GTIN-13, GTIN-14
 */
export function isValidGtin(gtin) {
    const cleanGtin = gtin.replace(/\D/g, "");
    if (![8, 12, 13, 14].includes(cleanGtin.length))
        return false;
    // Validate check digit using GS1 algorithm
    const paddedGtin = cleanGtin.padStart(14, "0");
    const digits = paddedGtin.split("").map(Number);
    const checkDigit = digits.pop();
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
        sum += digits[i] * (i % 2 === 0 ? 3 : 1);
    }
    const calculatedCheck = (10 - (sum % 10)) % 10;
    return calculatedCheck === checkDigit;
}
/**
 * Validate a GLN (Global Location Number)
 */
export function isValidGln(gln) {
    const cleanGln = gln.replace(/\D/g, "");
    if (cleanGln.length !== 13)
        return false;
    // Same check digit algorithm as GTIN-13
    return isValidGtin(cleanGln);
}
